import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";
import { useTrackedScreen } from "../../hooks/useTrackedScreen";
import {
  CERTIFIED_LOGO_MAX_HEIGHT,
  CERTIFIED_LOGO_MAX_WIDTH,
} from "@shared/app-certification";
import type { CSSProperties, ReactNode, RefObject } from "react";
import { useNow, formatTime } from "../_shared/useNow";
import { useFocusGrid, useScreenFocus } from "../_shared/useFocusGrid";
import { useBackHome } from "../_shared/useBackHome";
import { useRadioNowPlaying } from "../_shared/useRadioNowPlaying";
import { useRadioPlayback } from "../_shared/useRadioPlayback";
import { useInstagramMedia } from "../_shared/useInstagramMedia";
import type { InstagramPost } from "@shared/types/foyer";
import type {
  RealtimeEvent,
  SchemeTokens,
  SchemeApp,
  VilmerActivity,
} from "@shared/types/foyer";
import {
  SchemeProvider,
  useScheme,
  screenBackTarget,
  visibleHomeSections,
  visibleSecondarySections,
  type SchemeContextValue,
  type ResolvedMenuWeek,
  type ResolvedScheme,
} from "../_shared/schemeContext";
import { resolveLocalized } from "@shared/i18n";
import {
  ISO_WEEKDAYS_SV,
  ISO_WEEKDAYS_SV_SHORT,
  fromIsoDate,
  isoWeekDates,
  isoWeekParts,
  shiftIsoWeek,
  toIsoDate,
} from "@shared/isoWeek";
import { useMenuFeed } from "./useMenuFeed";
import { useVilmerActivities } from "./useVilmerActivities";
import {
  activitiesOn,
  activityDate,
  activityTimeRange,
  isFinished,
} from "@shared/vilmer";
import QRCode from "react-qr-code";
import { launchApp } from "../../lib/launchApp";
import { openTvForThisScreen } from "../../lib/openTv";
import { connectRealtime } from "../../lib/realtime";
import { getBinding } from "../../lib/binding";
import type { SchemeProps } from "../registry";
import { careManifest } from "@shared/schemes/manifests";
import { useRoomPhotos, type RoomPhotoWire } from "./useRoomPhotos";
import { useRoomWhiteboard } from "./useRoomWhiteboard";
import { useServiceRequests } from "./useServiceRequests";
import { useCallDirectory, callsHiddenForPremise } from "./useCallDirectory";
import { bridge } from "../../lib/foyerBridge";
import CustomScreen from "../_shared/custom/CustomScreen";
import { reconcileCall } from "../../lib/callSession";
import type { CallEndCause } from "./videocall/CallMediaSurface";
import CallVeil from "./videocall/CallVeil";
import { callTheme, videoPill } from "./videocall/callTheme";
import type {
  CareTaskCategory,
  CallEndReason,
  TvCallDirectoryEntry,
  TvCallDirectoryReason,
} from "@shared/types/foyer";

/** LiveKit is a heavy dependency no hospitality TV should carry; the media
 *  surface (and the SDK it pulls) only enters the bundle when a call is
 *  answered. See CallMediaSurface. */
const CallMediaSurface = lazy(() => import("./videocall/CallMediaSurface"));

/**
 * Hemvist - the care-segment launcher. Calm, high-contrast, warm paper +
 * deep green, Figtree + JetBrains Mono. Oversized type and dramatic focus
 * rings for elderly residents on a remote. Ported from the "Care kiosk
 * launcher" Claude Design handoff (launcher + Nöje / Radio / Info; the TV
 * tile just launches the TV app).
 */

/** Reconstruct the care palette from resolved tokens so operator overrides
 *  flow through every reference below. Defaults come from the manifest. */
function carePalette(t: SchemeTokens) {
  const e = { ...careManifest.defaultTokens.extras, ...t.extras };
  return {
    paper: t.palette.bg,
    paper2: e.paper2,
    surface: e.surface,
    surface2: e.surface2,
    hair: e.hair,
    hairSoft: t.palette.rule ?? "#E4E1D4",
    ink: t.palette.ink,
    inkSoft: t.palette.inkDim ?? "#5F665C",
    inkMuted: e.inkMuted,
    gDeep: t.palette.accent2 ?? "#1F3D2A",
    gCore: t.palette.accent,
    gSoft: e.gSoft,
    gTint: e.gTint,
    gGlow: e.gGlow,
    sans: t.fonts.sans ?? '"Figtree", system-ui, sans-serif',
    mono: t.fonts.mono ?? '"JetBrains Mono", ui-monospace, monospace',
  };
}
type Pal = ReturnType<typeof carePalette>;
function useC() {
  return carePalette(useScheme().tokens);
}

/** A slim focus ring for small buttons sitting side by side. */
const BUTTON_FOCUS = (p: Pal) => `0 0 0 3px ${p.paper}, 0 0 0 6px ${p.gCore}`;

const FOCUS_SHADOW = (p: Pal) =>
  `0 0 0 6px ${p.paper}, 0 0 0 14px ${p.gCore}, 0 50px 90px -30px rgba(31,61,42,0.45)`;

/**
 * What a destination is CALLED. Everything reads its name from {@link DEST},
 * except the operator-designed screen: that one is named by the premise (the
 * `customTitle` knob), so its tile, its header and its Back trail have to ask
 * the scheme rather than this table.
 */
function destLabel(scheme: ResolvedScheme, id: string): string {
  if (id === "custom" && scheme.knobs.has("customTitle")) {
    return scheme.knobs.text("customTitle");
  }
  return DEST[id]?.label ?? id;
}

type Screen =
  | "home"
  | "apps"
  | "radio"
  | "info"
  | "wifi"
  | "welcome"
  | "relatives"
  | "instagram"
  | "weather"
  | "menu"
  | "tv"
  | "photos"
  | "whiteboard"
  // "call" is the NURSE call (resident → staff). "videocall" is the resident
  // calling their family. Same word in Swedish, opposite direction - never
  // fold them together.
  | "call"
  | "videocall"
  // The operator-designed page. Named by the premise, not by us - the label on
  // its tile comes from the `customTitle` knob (see DEST below).
  | "custom";

// ── Swedish date for the header (the care kiosk is Swedish-first) ──────────
const DAYS_SV = [
  "Söndag",
  "Måndag",
  "Tisdag",
  "Onsdag",
  "Torsdag",
  "Fredag",
  "Lördag",
];
const MONTHS_SV = [
  "januari",
  "februari",
  "mars",
  "april",
  "maj",
  "juni",
  "juli",
  "augusti",
  "september",
  "oktober",
  "november",
  "december",
];
function careDate(d: Date): string {
  return `${DAYS_SV[d.getDay()]} ${d.getDate()} ${MONTHS_SV[d.getMonth()]} ${d.getFullYear()}`;
}

// ── Icons (geometric, per design) ──────────────────────────────────────────
type IconProps = { size?: number };
const stroke = {
  fill: "none" as const,
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};
const Icon = {
  Tv: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <rect x="2.5" y="4.5" width="19" height="13" rx="2" />
      <path d="M8 21h8M12 17.5V21" />
    </svg>
  ),
  Apps: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  Radio: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <path d="M4 9.5l13-5" />
      <rect x="3" y="9" width="18" height="11" rx="2" />
      <circle cx="16" cy="14.5" r="3" />
      <path d="M7 13v3" />
    </svg>
  ),
  Info: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 7.5v.5" />
    </svg>
  ),
  Arrow: ({ size = 30 }: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      {...stroke}
      strokeWidth={2.2}
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  Chevron: ({ size = 18 }: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      {...stroke}
      strokeWidth={2.4}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  ),
  Wifi: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <path d="M4.5 9.5a11 11 0 0 1 15 0" />
      <path d="M7.4 12.6a7 7 0 0 1 9.2 0" />
      <path d="M10 15.6a3 3 0 0 1 4 0" />
      <circle cx="12" cy="18.6" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  ),
  Welcome: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="M3.6 7 12 13l8.4-6" />
    </svg>
  ),
  Relatives: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <circle cx="8.5" cy="8.5" r="2.6" />
      <circle cx="16" cy="9.5" r="2.1" />
      <path d="M3.5 19c0-2.8 2.2-4.6 5-4.6s5 1.8 5 4.6" />
      <path d="M14.5 14.7c2.2.1 4 1.7 4 4.3" />
    </svg>
  ),
  Instagram: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17" cy="7" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  Weather: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <circle cx="9" cy="9.5" r="3.4" />
      <path d="M9 2.6v1.6M2.6 9.5H4M13.6 4.9l1 1" />
      <path d="M11 19a3.4 3.4 0 0 1 .4-6.8 4.1 4.1 0 0 1 7.8-.8 2.9 2.9 0 0 1 .4 7.6H11z" />
    </svg>
  ),
  Heart: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <path d="M12 20s-7-4.6-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.4-7 10-7 10z" />
    </svg>
  ),
  Star: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 17l-5.2 2.6 1-5.8L3.5 9.7l5.9-.9z" />
    </svg>
  ),
  Calendar: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M3.5 9.5h17M8 3v4M16 3v4" />
    </svg>
  ),
  Clock: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.2l3.4 2" />
    </svg>
  ),
  Phone: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <path d="M6.5 3.5l2.2.4 1.1 3.2-1.8 1.4a11 11 0 0 0 4.1 4.1l1.4-1.8 3.2 1.1.4 2.2a2 2 0 0 1-2 2.3A14 14 0 0 1 4.2 5.5a2 2 0 0 1 2.3-2z" />
    </svg>
  ),
  Book: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <path d="M5 4.5h9a2 2 0 0 1 2 2V20a2 2 0 0 0-2-2H5z" />
      <path d="M19 4.5h-1a2 2 0 0 0-2 2V18a2 2 0 0 1 2-2h1z" />
    </svg>
  ),
  Bell: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2H4.5z" />
      <path d="M10 20.5a2 2 0 0 0 4 0" />
    </svg>
  ),
  Leaf: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <path d="M5 19c-1-7 4-13 14-13 0 10-6 15-13 13z" />
      <path d="M5 19c3-4 6-6 10-7.5" />
    </svg>
  ),
  Sun: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2v2.4M12 19.6V22M2 12h2.4M19.6 12H22M4.9 4.9l1.7 1.7M17.4 17.4l1.7 1.7M19.1 4.9l-1.7 1.7M6.6 17.4l-1.7 1.7" />
    </svg>
  ),
  Coffee: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <path d="M4.5 8h12v5a5 5 0 0 1-5 5H9.5a5 5 0 0 1-5-5z" />
      <path d="M16.5 9.5H19a2.5 2.5 0 0 1 0 5h-2.5M7 3.5c-.6.8-.6 1.7 0 2.5M11 3.5c-.6.8-.6 1.7 0 2.5" />
    </svg>
  ),
  Music: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <path d="M9 18V6l10-2v12" />
      <circle cx="6.5" cy="18" r="2.5" />
      <circle cx="16.5" cy="16" r="2.5" />
    </svg>
  ),
  House: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <path d="M4 11l8-6.5 8 6.5" />
      <path d="M6 9.5V20h12V9.5" />
    </svg>
  ),
  Gift: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <rect x="4" y="9.5" width="16" height="11" rx="1.5" />
      <path d="M4 13h16M12 9.5v11M12 9.5C9 9.5 7 8.5 7 6.8 7 5.8 7.8 5 9 5c2 0 3 2 3 4.5C12 7 13 5 15 5c1.2 0 2 .8 2 1.8 0 1.7-2 2.7-5 2.7z" />
    </svg>
  ),
  Photo: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
      <circle cx="8.5" cy="10" r="2" />
      <path d="M2.5 16.5l4-4.5 3.5 4 3.5-5 5 6.5" />
    </svg>
  ),
  // Video call - a camera body with a lens, read as "see each other" rather
  // than the film-strip metaphor the Photo icon already owns.
  Video: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <rect x="2.5" y="6" width="13" height="12" rx="3" />
      <path d="M15.5 11l5-3.2v8.4l-5-3.2z" />
      <circle cx="9" cy="12" r="2.6" />
    </svg>
  ),
  // Matsedel - a cloche over a plate. Reads as "a served meal" at tile size,
  // where a knife-and-fork pair turns into two indistinguishable slivers.
  Meal: ({ size = 64 }: IconProps) => (
    <svg viewBox="0 0 24 24" width={size} height={size} {...stroke}>
      <path d="M3.5 17.5h17" />
      <path d="M5 17.5a7 7 0 0 1 14 0" />
      <path d="M12 10.5V8.6" />
      <circle cx="12" cy="7.4" r="1.1" />
    </svg>
  ),
};

const GLYPH: Record<string, (p: { size?: number }) => ReactNode> = {
  tv: Icon.Tv,
  apps: Icon.Apps,
  radio: Icon.Radio,
  info: Icon.Info,
  wifi: Icon.Wifi,
  welcome: Icon.Welcome,
  relatives: Icon.Relatives,
  instagram: Icon.Instagram,
  weather: Icon.Weather,
  photos: Icon.Photo,
  menu: Icon.Meal,
  vilmer: Icon.Calendar,
  meal: Icon.Meal,
  heart: Icon.Heart,
  star: Icon.Star,
  calendar: Icon.Calendar,
  clock: Icon.Clock,
  phone: Icon.Phone,
  book: Icon.Book,
  bell: Icon.Bell,
  leaf: Icon.Leaf,
  sun: Icon.Sun,
  coffee: Icon.Coffee,
  music: Icon.Music,
  house: Icon.House,
  gift: Icon.Gift,
  photo: Icon.Photo,
  whiteboard: Icon.Heart,
  call: Icon.Bell,
  videocall: Icon.Video,
};

/**
 * Launcher copy per destination. Counts come from live content.
 *
 * The shape a screen needs depends on WHERE the premise put it: a home tile
 * reads `label` + `kicker` + `desc`, a screen listed inside Information reads
 * `label` + `kicker`. Placement is an operator choice now, so anything the
 * manifest declares in `homeOrder.items` must carry all three - exported so a
 * test can hold the manifest and this table to that contract instead of
 * discovering a blank tile subtitle on a resident's TV.
 */
export const DEST: Record<
  string,
  { label: string; kicker: string; desc: (n: number | SchemeApp[]) => string }
> = {
  tv: {
    label: "TV",
    kicker: "Live & playback",
    desc: () => "Se på SVT, TV4 och dina favoritkanaler.",
  },
  apps: {
    label: "Nöje",
    kicker: "Appar & spel",
    desc: (appsOrCount) => {
      const apps = Array.isArray(appsOrCount) ? appsOrCount : [];
      if (apps.length === 0) return "Inga appar.";
      const first3 = apps.slice(0, 3).map((a) => a.name);
      const more = apps.length > 3 ? ` och mycket mer` : "";
      return `${apps.length} appar - ${first3.join(", ")}${more}.`;
    },
  },
  radio: {
    label: "Radio",
    kicker: "Sveriges Radio",
    desc: (n) => `${n} kanaler - P1, P2, P3, P4 och fler.`,
  },
  info: {
    label: "Info",
    kicker: "Boendet just nu",
    desc: () => "Dagens meny, aktiviteter och praktisk information.",
  },
  wifi: {
    label: "Wi-Fi",
    kicker: "Anslut",
    desc: () => "Nätverksnamn, lösenord och QR-kod att skanna.",
  },
  welcome: {
    label: "Välkommen",
    kicker: "Hälsning",
    desc: () => "Ett meddelande från personalen.",
  },
  relatives: {
    label: "Anhöriga",
    kicker: "För familjen",
    desc: () => "Besökstider, kontakt och nyheter för anhöriga.",
  },
  instagram: {
    label: "Instagram",
    kicker: "Följ oss",
    desc: () => "Skanna för att följa boendet på Instagram.",
  },
  weather: {
    label: "Väder",
    kicker: "Prognos",
    desc: () => "Vädret idag och de närmaste dagarna.",
  },
  photos: {
    label: "Foton",
    kicker: "Från familjen",
    desc: (n) => {
      const count = typeof n === "number" ? n : 0;
      return count > 0
        ? `${count} foto${count === 1 ? "" : "n"} från era nära och kära.`
        : "Foton skickade via e-post från familjen.";
    },
  },
  menu: {
    label: "Matsedel",
    kicker: "Veckans mat",
    // Matsedel lives under Info, so only `label` and `kicker` are ever read
    // (the secondary list and its preview panel). `desc` is here because DEST
    // requires it, and would be used if it ever became a home tile.
    desc: () => "Veckans måltider, måndag till söndag.",
  },
  vilmer: {
    label: "VilMer",
    kicker: "Aktiviteter",
    // Pinned inside Information (it renders IN the Information pane), so only
    // `label` and `kicker` are ever read. `desc` satisfies the DEST shape.
    desc: () => "Aktiviteter du kan vara med på, idag och den här veckan.",
  },
  custom: {
    // `label` is the FALLBACK only - the premise names this screen itself
    // (the `customTitle` knob), and `destLabel` reads that instead. It is here
    // because DEST requires it, and because a premise that clears the name
    // still needs a word on the tile.
    label: "Egen sida",
    kicker: "Från boendet",
    desc: () => "En sida boendet har gjort själva.",
  },
  whiteboard: {
    label: "Min vård",
    kicker: "Vårdöversikt",
    desc: () => "Ditt vårdteam, kost och viktig information.",
  },
  videocall: {
    label: "Videosamtal",
    kicker: "Ring familjen",
    desc: (n) => {
      const count = typeof n === "number" ? n : 0;
      return count > 0
        ? `Ring ${count === 1 ? "din kontakt" : `dina ${count} kontakter`} och se varandra.`
        : "Ring dina anhöriga och se varandra på TV:n.";
    },
  },
  call: {
    label: "Ring personal",
    kicker: "Hjälp",
    desc: (n) => {
      const count = typeof n === "number" ? n : 0;
      return count > 0
        ? `${count} pågående förfrågan${count === 1 ? "" : "ar"} - personal är på väg.`
        : "Tryck för att kalla på personal.";
    },
  },
};

export default function CareScheme({ scheme }: SchemeProps) {
  const [screen, setScreen] = useTrackedScreen<Screen>("home");
  const scopeRef = useRef<HTMLDivElement>(null);
  useScreenFocus(scopeRef);
  useBackHome(() => setScreen("home"));
  const p = carePalette(scheme.tokens);
  const { context } = scheme;

  // ── Realtime event bus (for photos_changed → slideshow refresh) ───────────
  const [lastEvent, setLastEvent] = useState<RealtimeEvent | null>(null);
  useEffect(() => {
    const binding = getBinding();
    if (!binding) return;
    return connectRealtime({
      premiseId: binding.premiseId,
      roomId: binding.roomId,
      screenToken: binding.screenToken,
      onEvent: (evt) => {
        if (
          evt.type === "photos_changed" ||
          evt.type === "care_profile_changed" ||
          evt.type === "care_task_changed" ||
          // VC-15: a relative was revoked / added / the policy moved - the
          // directory must reflect it now, not at the next poll.
          evt.type === "call_directory_changed" ||
          // VC-20: the outbound call the resident just placed moved - answered,
          // declined, timed out, or the callee hung up. OutboundCall folds this.
          evt.type === "call_state" ||
          evt.type === "call_invite"
        ) {
          setLastEvent(evt);
        }
      },
    });
  }, []);

  return (
    <SchemeProvider value={scheme}>
      <div
        ref={scopeRef}
        style={{
          position: "absolute",
          inset: 0,
          background: p.paper,
          color: p.ink,
          fontFamily: p.sans,
          fontWeight: 500,
          letterSpacing: "-0.005em",
          outline: "none",
          overflow: "hidden",
        }}
      >
        {screen === "home" && (
          <Home go={setScreen} scopeRef={scopeRef} context={context} />
        )}
        {screen === "apps" && (
          <Noje go={setScreen} scopeRef={scopeRef} context={context} />
        )}
        {screen === "radio" && (
          <Radio go={setScreen} scopeRef={scopeRef} context={context} />
        )}
        {screen === "info" && (
          <Info go={setScreen} scopeRef={scopeRef} context={context} />
        )}
        {screen === "wifi" && (
          <CareWifi go={setScreen} scopeRef={scopeRef} context={context} />
        )}
        {screen === "welcome" && (
          <CareWelcome go={setScreen} scopeRef={scopeRef} context={context} />
        )}
        {screen === "relatives" && (
          <CareRelatives go={setScreen} scopeRef={scopeRef} context={context} />
        )}
        {screen === "instagram" && (
          <CareInstagram go={setScreen} scopeRef={scopeRef} context={context} />
        )}
        {screen === "weather" && (
          <CareWeather go={setScreen} scopeRef={scopeRef} context={context} />
        )}
        {screen === "menu" && (
          <CareMenu go={setScreen} scopeRef={scopeRef} context={context} />
        )}
        {screen === "tv" && (
          <TvLaunch go={setScreen} scopeRef={scopeRef} context={context} />
        )}
        {screen === "photos" && (
          <PhotoSlideshow
            go={setScreen}
            scopeRef={scopeRef}
            context={context}
            realtimeEvent={lastEvent}
          />
        )}
        {screen === "whiteboard" && (
          <Whiteboard
            go={setScreen}
            scopeRef={scopeRef}
            context={context}
            realtimeEvent={lastEvent}
          />
        )}
        {screen === "call" && scheme.knobs.toggle("showCall") && (
          <CallStaff
            go={setScreen}
            scopeRef={scopeRef}
            context={context}
            realtimeEvent={lastEvent}
          />
        )}
        {/* No knob guard (VC2-8): the screen is only reachable from a tile that
            the server's answer already gated, and it ejects itself to Home if
            that answer turns into a premise-level "no" while it is open. */}
        {screen === "custom" && (
          <CareCustom go={setScreen} scopeRef={scopeRef} context={context} />
        )}
        {screen === "videocall" && (
          <VideoCallDirectory
            go={setScreen}
            scopeRef={scopeRef}
            context={context}
            realtimeEvent={lastEvent}
          />
        )}
      </div>
    </SchemeProvider>
  );
}

// ── Chrome ─────────────────────────────────────────────────────────────────

function Head({ context }: { context: SchemeContextValue }) {
  const p = useC();
  const now = useNow();
  const logoUrl = useScheme().content.branding.logoUrl;
  return (
    <header
      style={{
        flex: "0 0 auto",
        height: 132,
        padding: "0 72px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid ${p.hairSoft}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={context.hotelName}
            style={{ height: 72, maxWidth: 420, objectFit: "contain" }}
          />
        ) : (
          <>
            <span
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: p.gDeep,
                display: "grid",
                placeItems: "center",
              }}
              aria-hidden="true"
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "14px 4px 14px 4px",
                  background: p.gGlow,
                  transform: "rotate(-25deg)",
                }}
              />
            </span>
            <span
              style={{
                fontSize: 38,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: p.ink,
              }}
            >
              {context.hotelName}
            </span>
          </>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 24 }}>
        <span style={{ fontSize: 26, color: p.inkSoft }}>{careDate(now)}</span>
        <span
          style={{
            fontSize: 56,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "-0.03em",
            color: p.ink,
          }}
        >
          {formatTime(now)}
        </span>
      </div>
    </header>
  );
}

function Hint({
  keys,
  right,
  noBorder = false,
}: {
  keys: Array<{ cap: string; label: string }>;
  right?: string;
  noBorder?: boolean;
}) {
  const p = useC();
  return (
    <footer
      style={{
        flex: "0 0 auto",
        height: 76,
        padding: "0 72px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderTop: noBorder ? "none" : `1px solid ${p.hairSoft}`,
        fontSize: 22,
        color: p.inkSoft,
      }}
    >
      <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
        {keys.map((k, i) => (
          <span
            key={i}
            style={{ display: "inline-flex", alignItems: "center", gap: 12 }}
          >
            <span
              style={{
                display: "inline-grid",
                placeItems: "center",
                minWidth: 44,
                height: 36,
                padding: "0 10px",
                borderRadius: 8,
                background: p.surface,
                border: `1px solid ${p.hair}`,
                fontSize: 18,
                fontWeight: 600,
                color: p.ink,
                fontFamily: p.mono,
              }}
            >
              {k.cap}
            </span>
            <span>{k.label}</span>
          </span>
        ))}
      </div>
      <span
        style={{
          fontFamily: p.mono,
          fontSize: 16,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: p.inkMuted,
        }}
      >
        {right ?? "omsorgsskärm"}
      </span>
    </footer>
  );
}

interface ScreenProps {
  go: (s: Screen) => void;
  scopeRef: RefObject<HTMLDivElement>;
  context: SchemeContextValue;
}

// Pill shown in submenu titlebars (the actual Back is D-pad ← / OK on it via onBack).
function BackPill({ focused = false }: { focused?: boolean }) {
  const p = useC();
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 22px",
        borderRadius: 999,
        background: focused ? p.gCore : p.surface,
        border: `1px solid ${focused ? p.gCore : p.hairSoft}`,
        color: focused ? "#fff" : p.inkSoft,
        fontSize: 22,
        fontWeight: 600,
        boxShadow: focused
          ? `0 0 0 4px ${p.paper}, 0 0 0 10px ${p.gCore}`
          : "none",
        transform: focused ? "translateY(-1px)" : "none",
        transition: "transform 120ms ease",
      }}
    >
      <span
        style={{
          fontFamily: p.mono,
          fontSize: 14,
          padding: "3px 8px",
          borderRadius: 6,
          border: `1px solid ${focused ? "rgba(255,255,255,0.4)" : p.hair}`,
          background: focused ? "rgba(255,255,255,0.18)" : p.paper,
          color: focused ? "#fff" : p.ink,
        }}
      >
        ◀
      </span>
      Tillbaka
    </span>
  );
}

function SubTitle({
  eyebrow,
  title,
  right,
  backFocused = false,
}: {
  eyebrow: string;
  title: string;
  right?: string;
  backFocused?: boolean;
}) {
  const p = useC();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 24,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 28 }}>
        <BackPill focused={backFocused} />
        <div>
          <div
            style={{
              fontFamily: p.mono,
              fontSize: 15,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: p.inkMuted,
            }}
          >
            {eyebrow}
          </div>
          <h1
            style={{
              fontSize: 76,
              fontWeight: 700,
              letterSpacing: "-0.035em",
              lineHeight: 1,
              color: p.ink,
              margin: "4px 0 0",
            }}
          >
            {title}
          </h1>
        </div>
      </div>
      {right && (
        <span
          style={{
            fontFamily: p.mono,
            fontSize: 15,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: p.inkMuted,
          }}
        >
          {right}
        </span>
      )}
    </div>
  );
}

const subMain: CSSProperties = {
  flex: "1 1 auto",
  padding: "48px 72px",
  display: "flex",
  flexDirection: "column",
  gap: 36,
  minHeight: 0,
};

// ── HOME (launcher) ──────────────────────────────────────────────────────

function Home({ go, scopeRef, context }: ScreenProps) {
  const scheme = useScheme();

  // In-room care whiteboard - the "Min vård" tile only appears when the room's
  // resident actually has a care profile authored (occupied + profile present).
  const { projection: whiteboard } = useRoomWhiteboard(
    true,
    undefined,
    context.premiseId,
    context.roomId
  );
  const hasWhiteboard = !!whiteboard?.profile;

  // "Ring personal" is operator-controlled (Components → Toggles). When it's
  // off we neither show the tile nor poll for pending requests.
  const showCall = scheme.knobs.toggle("showCall");

  // Live pending-request count for the "Ring personal" tile.
  const { pending } = useServiceRequests(
    showCall,
    undefined,
    context.premiseId,
    context.roomId
  );

  // Videosamtal (VC-15, VC2-8). There is no operator "show it" knob: the server
  // owns the answer, because everything that decides it - the premise calling
  // policy, the resident's seat, their opt-out, whether anyone lives here -
  // lives there. A premise-level "no" (calls off, outbound off, empty room, no
  // seat) removes the tile entirely: a button a resident can press but nobody
  // in the room can fix is worse than no button. Withheld until the first
  // answer lands, so we never flash a dead tile.
  const {
    entries: callableFaces,
    reason: directoryReason,
    ready: directoryReady,
    unavailable: directoryUnavailable,
  } = useCallDirectory(true, undefined, context.premiseId, context.roomId);
  // Fail-closed: `directoryUnavailable` (we never got a real answer, e.g. the
  // CMS preview's session-gated fetch failing) must hide the tile exactly
  // like every other "no" in PREMISE_LEVEL_OFF - it must NOT be conflated with
  // `reason: null`, which means the server answered and nothing hides it.
  const videoCallVisible =
    directoryReady &&
    !directoryUnavailable &&
    !callsHiddenForPremise(directoryReason);

  const tiles = [
    { id: "tv", go: "tv" as Screen },
    ...(showCall ? [{ id: "call", go: "call" as Screen }] : []),
    ...visibleHomeSections(scheme)
      .filter((id) => id !== "videocall" || videoCallVisible)
      .map((id) => ({ id, go: id as Screen })),
    ...(hasWhiteboard
      ? [{ id: "whiteboard", go: "whiteboard" as Screen }]
      : []),
  ];
  const homeIcons = scheme.content.homeIcons ?? {};
  const homeCards = scheme.content.homeCards ?? {};

  // Live photo count for the photos tile - fetched on mount, refreshes on
  // photos_changed. Rendered separately from the slideshow poll so the home
  // screen always shows an accurate count even before the user taps in.
  const { photos: homeFeedPhotos } = useRoomPhotos(
    tiles.some((t) => t.id === "photos"),
    undefined,
    context.premiseId,
    context.roomId
  );

  const counts: Record<string, number> = {
    apps: scheme.content.apps.length,
    radio: scheme.content.stations.length,
    info: scheme.content.infoSections.length,
    photos: homeFeedPhotos.length,
    call: pending.length,
    videocall: callableFaces.length,
  };

  const cols = 2;
  const rows = Math.ceil(tiles.length / cols);
  const [focus] = useFocusGrid({
    rows,
    cols,
    scopeRef,
    wrap: true,
    onEnter: (f) => {
      const t = tiles[f.r * cols + f.c];
      if (t) go(t.go);
    },
  });
  const focusIdx = focus.r * cols + focus.c;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main
        style={{
          flex: "1 1 auto",
          padding: "60px 72px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridAutoRows: "1fr",
          gap: 40,
          minHeight: 0,
        }}
      >
        {tiles.map((t, i) => (
          <Tile
            key={t.id}
            id={t.id}
            icon={homeIcons[t.id]}
            title={homeCards[t.id]?.title || destLabel(scheme, t.id)}
            desc={
              homeCards[t.id]?.description ||
              // A tile reads label + kicker + desc; a screen listed inside
              // Information reads label + kicker only. Placement is now an
              // operator choice, so a screen promoted to the home screen could
              // land here with no `desc` written for it - fall back to the
              // kicker rather than render a tile with a blank subtitle. (The
              // manifest test keeps every draggable id supplied with both.)
              DEST[t.id]?.desc(
                t.id === "apps" ? scheme.content.apps : (counts[t.id] ?? 0)
              ) ||
              DEST[t.id]?.kicker ||
              ""
            }
            focus={i === focusIdx}
          />
        ))}
      </main>
    </div>
  );
}

const TILE_TITLE_MAX = 108; // hero size - great for "TV"/"Info"
const TILE_TITLE_MIN = 44; // floor so nothing turns illegible

// Shrink the tile title to fit on ONE line inside its column. The old fixed
// 108px looked great for "TV"/"Info" but longer labels - "Ring personal",
// "Videosamtal", "Anhöriga" - wrapped to two lines, and on the shorter cards
// of a 5-6 tile grid that pushed the description + arrow past the card's
// overflow:hidden edge (they got clipped). Char-width guesses proved
// unreliable across fonts, so measure the real rendered width and scale down.
function useFitTitle(title: string) {
  const ref = useRef<HTMLHeadingElement>(null);
  const [size, setSize] = useState(TILE_TITLE_MAX);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      // Measure the intrinsic single-line width at max size, then scale to the
      // available content-box width. One pass: font size is linear in width.
      el.style.fontSize = `${TILE_TITLE_MAX}px`;
      const avail = el.clientWidth;
      const needed = el.scrollWidth;
      if (avail > 0 && needed > avail) {
        const next = Math.max(
          TILE_TITLE_MIN,
          Math.floor(TILE_TITLE_MAX * (avail / needed))
        );
        setSize(next);
        el.style.fontSize = `${next}px`;
      } else {
        setSize(TILE_TITLE_MAX);
      }
    };
    fit();
    // Re-fit when the column resizes (CMS preview, different tile counts).
    // Guard on width only: fit() mutates font size, which changes the h2's
    // *height* - reacting to that would loop. Width is parent-constrained and
    // stable across our font tweak, so it only moves on real layout changes.
    let lastW = el.clientWidth;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      if (Math.abs(w - lastW) < 1) return;
      lastW = w;
      fit();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [title]);
  return { ref, size };
}

function Tile({
  id,
  icon,
  title,
  desc,
  focus,
}: {
  id: string;
  icon?: string;
  title: string;
  desc: string;
  focus: boolean;
}) {
  const p = useC();
  const { ref: titleRef, size: titleSize } = useFitTitle(title);
  // Per-tile visual variant (icon frame + glyph).
  const variant: Record<
    string,
    {
      frameBg: string;
      frameInk: string;
      glyphBg: string;
      glyphBorder: string;
      glyphInk: string;
    }
  > = {
    tv: {
      frameBg: `radial-gradient(120% 80% at 0% 100%, rgba(255,255,255,0.18), transparent 60%), ${p.gDeep}`,
      frameInk: p.gGlow,
      glyphBg: "rgba(255,255,255,0.08)",
      glyphBorder: "rgba(255,255,255,0.18)",
      glyphInk: p.gGlow,
    },
    apps: {
      frameBg: p.gTint,
      frameInk: p.gDeep,
      glyphBg: p.surface,
      glyphBorder: p.hairSoft,
      glyphInk: p.gCore,
    },
    radio: {
      frameBg: p.surface2,
      frameInk: p.gDeep,
      glyphBg: p.gTint,
      glyphBorder: p.gTint,
      glyphInk: p.gDeep,
    },
    info: {
      frameBg: p.paper2,
      frameInk: p.ink,
      glyphBg: p.ink,
      glyphBorder: p.ink,
      glyphInk: p.paper,
    },
  };
  const v = variant[id] ?? variant.info;
  const Glyph = GLYPH[icon ?? id] ?? GLYPH[id] ?? Icon.Info;
  return (
    <article
      style={{
        position: "relative",
        borderRadius: 32,
        background: p.surface,
        border: `1px solid ${focus ? "transparent" : p.hairSoft}`,
        boxShadow: focus
          ? FOCUS_SHADOW(p)
          : "0 1px 0 rgba(23,25,21,0.04), 0 8px 24px -12px rgba(23,25,21,0.08)",
        overflow: "hidden",
        display: "grid",
        // minmax(0, 1fr) pins the content track to the flexible width instead
        // of letting a nowrap title's min-content blow the column out.
        gridTemplateColumns: "320px minmax(0, 1fr)",
        transform: focus ? "translateY(-4px)" : "none",
        transition: "transform 200ms ease",
      }}
    >
      <div style={{ position: "relative" }}>
        <div
          style={{
            position: "absolute",
            inset: 24,
            borderRadius: 22,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: 26,
            background: v.frameBg,
            color: v.frameInk,
            border: id === "radio" ? `1px solid ${p.hairSoft}` : undefined,
            overflow: "hidden",
          }}
        >
          <span
            style={{
              alignSelf: "flex-start",
              width: 110,
              height: 110,
              borderRadius: 22,
              display: "grid",
              placeItems: "center",
              background: v.glyphBg,
              border: `1px solid ${v.glyphBorder}`,
              color: v.glyphInk,
            }}
          >
            <Glyph size={64} />
          </span>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "36px 44px 32px 44px",
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0, overflow: "hidden" }}>
          <h2
            ref={titleRef}
            style={{
              fontSize: titleSize,
              fontWeight: 700,
              // >=1.2 so the line box clears descenders (g/y/p/j) - this sits
              // inside an overflow:hidden wrapper, so anything tighter gets
              // chopped off at the bottom instead of just looking cramped.
              lineHeight: 1.2,
              letterSpacing: "-0.04em",
              margin: 0,
              color: p.ink,
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {title}
          </h2>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <p
            style={{
              fontSize: 25,
              lineHeight: 1.3,
              color: p.inkSoft,
              maxWidth: 460,
              fontWeight: 500,
              margin: 0,
            }}
          >
            {desc}
          </p>
          <span
            style={{
              width: 64,
              height: 64,
              borderRadius: 999,
              display: "grid",
              placeItems: "center",
              background: focus ? p.gCore : p.paper,
              border: `1px solid ${focus ? p.gCore : p.hairSoft}`,
              color: focus ? "#fff" : p.ink,
              flex: "0 0 auto",
            }}
          >
            <Icon.Arrow size={30} />
          </span>
        </div>
      </div>
    </article>
  );
}

// ── NÖJE (apps) ─────────────────────────────────────────────────────────

function Noje({ go, scopeRef, context }: ScreenProps) {
  const p = useC();
  // Certified apps are already dropped for a care premise upstream, in
  // `withGlobalApps` (NFLX-6). This second filter is deliberate belt-and-braces
  // rather than a duplicated rule: everywhere else a stale config costs an
  // ordering or a missing tile, but here it would put a streaming app under a
  // vendor agreement onto a hospital television - and `certified` is already on
  // the app, so the check costs nothing and can never disagree with the merge.
  const apps = useScheme()
    .content.apps.filter((a) => a.enabled !== false)
    .filter((a) => !a.certified);
  const cols = 4;
  const contentRows = Math.max(1, Math.ceil(apps.length / cols));
  // Row 0 is the Tillbaka pill; the app grid starts at row 1.
  const [focus] = useFocusGrid({
    rows: contentRows + 1,
    cols,
    scopeRef,
    initial: { r: 1, c: 0 },
    wrap: true,
    onEnter: (f) => {
      if (f.r === 0) {
        go("home");
        return;
      }
      const a = apps[(f.r - 1) * cols + f.c];
      if (a) launchApp(a);
    },
    onBack: () => go("home"),
  });
  const backFocused = focus.r === 0;
  const focusIdx = backFocused ? -1 : (focus.r - 1) * cols + focus.c;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main style={subMain}>
        <SubTitle
          eyebrow="02 · Nöje"
          title="Dina appar"
          right={`${apps.length} tillgängliga`}
          backFocused={backFocused}
        />
        <div
          style={{
            flex: "1 1 auto",
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            // Was a fixed 200px row; the cards carry their own 16:9 now
            // (NFLX-5) and a fixed row height would override it.
            gridAutoRows: "auto",
            alignContent: "start",
            gap: 28,
            minHeight: 0,
          }}
        >
          {apps.map((a, i) => {
            const focused = i === focusIdx;
            // No-logo fallback: the brand colour reads on a white tile (white
            // if that's the brand's own colour → fall back to text, then ink).
            const notWhite = (c?: string) =>
              !!c && !["#fff", "#ffffff", "white"].includes(c.toLowerCase());
            const brandInk = notWhite(a.color)
              ? a.color
              : notWhite(a.text)
                ? a.text
                : p.ink;
            return (
              <article
                key={a.id}
                onClick={() => launchApp(a)}
                style={{
                  // Same rule as the hotel templates: a white tile (so brand
                  // logos read - a Netflix logo on a coloured tile never works),
                  // landscape, logo only.
                  background: p.surface,
                  border: `1px solid ${focused ? "transparent" : p.hairSoft}`,
                  borderRadius: 28,
                  // 16:9, matching the hotel templates (NFLX-5). Care shows no
                  // certified app today - `dueActions` excludes care outright -
                  // but a grid that drifts from the others is a grid that has
                  // to be re-reasoned about every time this rule changes.
                  aspectRatio: "16 / 9",
                  // border-box, or the ratio governs the CONTENT box and the padding is
                  // added on top - which renders 1.65:1, not 16:9. Measured, not assumed.
                  boxSizing: "border-box",
                  display: "grid",
                  placeItems: "center",
                  padding: 28,
                  cursor: "pointer",
                  boxShadow: focused ? FOCUS_SHADOW(p) : "none",
                  transform: focused ? "translateY(-2px)" : "none",
                  transition: "transform 160ms ease",
                }}
              >
                {a.logoUrl ? (
                  <img
                    src={a.logoUrl}
                    alt={a.name}
                    style={{
                      maxWidth: a.certified ? CERTIFIED_LOGO_MAX_WIDTH : "72%",
                      maxHeight: a.certified
                        ? CERTIFIED_LOGO_MAX_HEIGHT
                        : "60%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: 44,
                      fontWeight: 800,
                      letterSpacing: "-0.02em",
                      color: brandInk,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "90%",
                    }}
                  >
                    {a.wordmark || a.name}
                  </span>
                )}
              </article>
            );
          })}
        </div>
      </main>
      <Hint
        keys={[
          { cap: "◀▶▲▼", label: "Välj app" },
          { cap: "OK", label: "Öppna" },
          { cap: "◀", label: "Tillbaka" },
        ]}
      />
    </div>
  );
}

// ── RADIO ───────────────────────────────────────────────────────────────

const WAVE = [
  22, 38, 55, 42, 28, 60, 48, 34, 52, 40, 30, 46, 62, 38, 26, 50, 34, 58, 42,
  30, 48, 36, 52, 40, 26, 60, 44, 32, 50, 38, 28, 56, 42, 30, 48,
];

function Radio({ go, scopeRef, context }: ScreenProps) {
  const p = useC();
  const stations = useScheme().content.stations;
  // Audio playback (start on OK / stop on leave). `playingId` is null until the
  // guest OKs a channel - before that the big pane just *previews* the focused.
  const { playingId, toggle, audioRef } = useRadioPlayback(stations);
  // Row 0 is the Tillbaka pill; stations start at row 1.
  const [focus] = useFocusGrid({
    rows: stations.length + 1,
    cols: 1,
    scopeRef,
    initial: { r: 1, c: 0 },
    wrap: true,
    onEnter: (f) => {
      if (f.r === 0) go("home");
      else toggle(stations[f.r - 1]?.id);
    },
    onBack: () => go("home"),
  });
  const backFocused = focus.r === 0;
  const stationIdx = backFocused ? 0 : focus.r - 1;
  // The big pane follows FOCUS (hover) - a preview of the highlighted channel.
  const cur = stations[stationIdx] ?? stations[0];
  const isPlaying = !!cur && cur.id === playingId;
  // Keep the focused row visible when the list is taller than the pane.
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!backFocused) rowRef.current?.scrollIntoView({ block: "nearest" });
  }, [stationIdx, backFocused]);
  // Live now-playing per station (Sveriges Radio); falls back to static copy.
  const np = useRadioNowPlaying(stations.map((s) => s.metadataId));
  const curLive = cur?.metadataId ? np[cur.metadataId] : undefined;
  const curProgram = curLive?.program ?? cur?.nowPlaying;
  const curSong = curLive?.song;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <audio ref={audioRef} preload="none" />
      <Head context={context} />
      <main style={subMain}>
        <SubTitle
          eyebrow="03 · Radio"
          title="Sveriges Radio"
          right="FM · DAB+ · Streaming"
          backFocused={backFocused}
        />
        <div
          style={{
            flex: "1 1 auto",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 32,
            minHeight: 0,
          }}
        >
          {/* Now playing */}
          <section
            style={{
              background: p.gDeep,
              color: "#fff",
              borderRadius: 32,
              padding: "44px 48px",
              display: "flex",
              flexDirection: "column",
              gap: 28,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                fontFamily: p.mono,
                fontSize: 16,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: p.gGlow,
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 999,
                  background: isPlaying ? p.gGlow : "transparent",
                  border: isPlaying ? "none" : `2px solid ${p.gGlow}`,
                  boxShadow: isPlaying
                    ? `0 0 0 6px rgba(199,221,184,0.2)`
                    : "none",
                  animation: isPlaying
                    ? "pulseGlow 2s ease-in-out infinite"
                    : "none",
                }}
              />
              {isPlaying ? "Spelar nu" : "Förhandsvisning"} ·{" "}
              {String(stationIdx + 1).padStart(2, "0")} av{" "}
              {String(stations.length).padStart(2, "0")}
            </span>
            <h2
              style={{
                fontSize: 124,
                fontWeight: 700,
                letterSpacing: "-0.04em",
                // >=1.2 - the station name is free text (can carry
                // descenders) inside this card's overflow:hidden box.
                lineHeight: 1.2,
                color: "#fff",
                margin: 0,
              }}
            >
              {cur?.name}
              {cur?.tag && (
                <small
                  style={{
                    fontSize: 36,
                    fontWeight: 500,
                    color: p.gGlow,
                    letterSpacing: "-0.02em",
                    display: "block",
                    marginTop: 8,
                  }}
                >
                  {cur.tag}
                </small>
              )}
            </h2>
            {(curProgram || curSong) && (
              <div>
                {curProgram && (
                  <div
                    style={{
                      fontSize: 36,
                      fontWeight: 600,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {curProgram}
                  </div>
                )}
                {curSong && (
                  <p
                    style={{
                      fontSize: 24,
                      color: "rgba(255,255,255,0.78)",
                      lineHeight: 1.35,
                      margin: "10px 0 0",
                    }}
                  >
                    ♪ {curSong}
                  </p>
                )}
              </div>
            )}
            <div
              style={{
                position: "absolute",
                right: -10,
                bottom: 32,
                left: 32,
                height: 72,
                display: "flex",
                alignItems: "flex-end",
                gap: 6,
                opacity: 0.55,
                pointerEvents: "none",
              }}
              aria-hidden="true"
            >
              {WAVE.map((h, i) => (
                <span
                  key={i}
                  style={{
                    flex: 1,
                    height: h,
                    background: p.gGlow,
                    borderRadius: 4,
                  }}
                />
              ))}
            </div>
          </section>

          {/* Station list */}
          <section
            className="foyer-scheme-scroll"
            style={{
              background: p.surface,
              border: `1px solid ${p.hairSoft}`,
              borderRadius: 32,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "28px 32px 18px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
              }}
            >
              <span
                style={{
                  fontSize: 30,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: p.ink,
                }}
              >
                Kanaler
              </span>
              <span
                style={{
                  fontFamily: p.mono,
                  fontSize: 14,
                  color: p.inkMuted,
                  letterSpacing: "0.12em",
                }}
              >
                {stations.length} kanaler
              </span>
            </div>
            <div
              style={{ flex: "1 1 auto", overflow: "auto" }}
              className="foyer-scheme-scroll"
            >
              {stations.map((s, i) => {
                const focused = !backFocused && i === stationIdx;
                const sLive = s.metadataId ? np[s.metadataId] : undefined;
                const sLine = sLive?.song ?? sLive?.program ?? s.nowPlaying;
                return (
                  <div
                    key={s.id}
                    ref={focused ? rowRef : undefined}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "80px 1fr auto",
                      alignItems: "center",
                      gap: 24,
                      padding: "22px 32px",
                      borderTop: `1px solid ${p.hairSoft}`,
                      background: focused ? p.gTint : "transparent",
                      position: "relative",
                    }}
                  >
                    {focused && (
                      <span
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 6,
                          background: p.gCore,
                        }}
                      />
                    )}
                    {s.id === playingId ? (
                      <span
                        aria-label="Spelar"
                        style={{
                          justifySelf: "center",
                          width: 14,
                          height: 14,
                          borderRadius: 999,
                          background: p.gCore,
                          boxShadow: `0 0 0 5px ${p.gTint}`,
                          animation: "pulseGlow 2s ease-in-out infinite",
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          fontFamily: p.mono,
                          fontSize: 22,
                          fontWeight: 700,
                          color: p.gCore,
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    )}
                    <div>
                      <div
                        style={{
                          fontSize: 28,
                          fontWeight: 700,
                          letterSpacing: "-0.02em",
                          color: p.ink,
                        }}
                      >
                        {s.name}
                      </div>
                      {sLine && (
                        <div
                          style={{
                            fontSize: 18,
                            color: p.inkSoft,
                            marginTop: 2,
                          }}
                        >
                          {sLine}
                        </div>
                      )}
                    </div>
                    <span
                      style={{
                        fontFamily: p.mono,
                        fontSize: 14,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: p.inkMuted,
                      }}
                    >
                      {s.tag}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </main>
      <Hint
        keys={[
          { cap: "▲▼", label: "Välj kanal" },
          { cap: "OK", label: "Spela" },
          { cap: "◀", label: "Tillbaka" },
        ]}
      />
    </div>
  );
}

// ── VILMER (activities, inside the Information pane) ────────────────────────

/**
 * VilMer's activity calendar, rendered in the right-hand half of Information.
 *
 * ## Why it lives in the pane instead of being its own screen
 *
 * Explicit product call: focusing the VilMer row must already show the
 * activities - no click to open. That is how an INFO SECTION behaves in this
 * screen (the pane follows focus), and not how a secondary screen behaves (a
 * "Tryck OK" stub). VilMer is a secondary screen by every other measure - it
 * has a knob, it is placed by `infoOrder` - so it takes the section's
 * behaviour without becoming one, and `mode` is what buys that:
 *
 *   "list"   the left menu owns the remote; this pane is a live preview
 *   "pane"   OK was pressed; the remote now walks the activities
 *   "detail" OK again; one activity fills the pane
 *
 * Each mode owns its own `useFocusGrid`, gated by `enabled`, because the hook
 * binds one window-level key listener per grid and two live grids would both
 * act on the same press. This is the only screen in the care template that
 * hands focus between two panes, which is exactly why the state is three named
 * values rather than a pair of booleans.
 *
 * ## Why the range is fixed at 14 days and the toggle is client-side
 *
 * The fetch window is "today plus 13", identical for every TV in the building
 * for the whole day. VilMer issue one token per ward or per company as often as
 * per room, so the server caches on the token - and a rolling or per-view range
 * would give every screen a slightly different key and turn one upstream call
 * into forty. Switching Dag/Vecka therefore filters what is already in hand and
 * costs nothing.
 *
 * ## Why there is no week NUMBER anywhere
 *
 * Matsedel is keyed on ISO week because the kitchen's paper schedule is. An
 * activity programme is not: a resident asks "what is on today" and "what is on
 * this week", never "what is on in week 32". So Vecka means the next seven
 * days, headed "Idag" / "Imorgon" / a weekday, and no arithmetic can put a
 * resident on the wrong side of a year boundary.
 */

/** How far ahead the pane can look. Two weeks - decision: no browsing
 *  backwards, nobody needs last Tuesday's programme. */
const VILMER_DAYS = 14;

type VilmerMode = "list" | "pane" | "detail";

/** Category → glyph. Free text upstream, so unknown values fall back rather
 *  than rendering nothing where every other row has an icon. */
const VILMER_GLYPH: Record<string, (p: { size?: number }) => ReactNode> = {
  rorelse: Icon.Heart,
  kultur: Icon.Book,
  social: Icon.Relatives,
  musik: Icon.Music,
  andlighet: Icon.Star,
  utomhus: Icon.Leaf,
  skapande: Icon.Gift,
  omsorg: Icon.Heart,
  mat: Icon.Coffee,
};

function vilmerGlyph(category?: string) {
  return VILMER_GLYPH[(category ?? "").toLowerCase()] ?? Icon.Calendar;
}

function addDays(iso: string, n: number): string {
  const d = fromIsoDate(iso);
  if (!d) return iso;
  d.setDate(d.getDate() + n);
  return toIsoDate(d);
}

/** "Idag" / "Imorgon" / "torsdag 7 augusti" - a resident reads the first two
 *  instantly and needs the third only further out. */
function vilmerDayLabel(iso: string, today: string): string {
  if (iso === today) return "Idag";
  if (iso === addDays(today, 1)) return "Imorgon";
  const d = fromIsoDate(iso);
  if (!d) return iso;
  return `${DAYS_SV[d.getDay()]} ${d.getDate()} ${MONTHS_SV[d.getMonth()]}`;
}

interface VilmerPaneProps {
  mode: VilmerMode;
  setMode: (m: VilmerMode) => void;
  scopeRef: RefObject<HTMLElement>;
  context: SchemeContextValue;
}

function VilmerPane({ mode, setMode, scopeRef, context }: VilmerPaneProps) {
  const p = useC();
  const now = useNow();
  const today = toIsoDate(now);
  // The window is anchored on the day the component mounted rather than on
  // `now`, so a TV left on across midnight cannot silently re-key the server
  // cache at 00:00 and re-fetch for the whole building at once.
  const [anchor] = useState(today);
  const range = {
    from: anchor,
    to: addDays(anchor, VILMER_DAYS - 1),
  };
  const { result, loading, failed } = useVilmerActivities(
    true,
    range,
    context.premiseId,
    // A CMS preview mounts as the synthetic room "preview", which matches no
    // row and would report itself unconfigured however well the premise is
    // wired. Borrow a real room's token there so an operator checking their own
    // work sees what a resident will see.
    context.previewRoomId ?? context.roomId
  );

  const [view, setView] = useState<"day" | "week">("day");
  /** A logo URL that 404s must not leave a broken-image box on a wall. One
   *  flag for the pane: if it failed once it will fail again this session. */
  const [logoFailed, setLogoFailed] = useState(false);
  /** Same, per activity picture - keyed by id so one bad photo doesn't blank
   *  every other row's thumbnail. */
  const [badImages, setBadImages] = useState<Record<string, true>>({});
  const imageOf = (a: VilmerActivity) =>
    a.imageUrl && !badImages[a.id] ? a.imageUrl : undefined;
  const markBadImage = (id: string) =>
    setBadImages((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  /** Days from the anchor. Day view steps by 1, week view by 7. */
  const [offset, setOffset] = useState(0);
  const [detailId, setDetailId] = useState<string | null>(null);

  const activities = result?.activities ?? [];
  const dayIso = addDays(anchor, offset);
  const days =
    view === "day"
      ? [dayIso]
      : Array.from({ length: 7 }, (_, i) => addDays(dayIso, i)).filter(
          (d) => d <= range.to
        );
  // Flat, in render order, so ONE focus index addresses the whole pane whether
  // it is showing a single day or a whole week.
  const rows = days.flatMap((d) => activitiesOn(activities, d));
  const maxOffset = view === "day" ? VILMER_DAYS - 1 : VILMER_DAYS - 7;

  // --- Pane focus: row 0 is the control strip, rows 1..n the activities ---
  const [paneFocus, setPaneFocus] = useFocusGrid({
    rows: rows.length + 1,
    cols: 4,
    initial: { r: rows.length ? 1 : 0, c: 0 },
    scopeRef,
    wrap: false,
    enabled: mode === "pane",
    onEnter: (f) => {
      if (f.r === 0) {
        if (f.c === 0) {
          setView("day");
          setOffset(0);
        } else if (f.c === 1) {
          setView("week");
          setOffset(0);
        } else if (f.c === 2) {
          setOffset((o) => Math.max(0, o - (view === "day" ? 1 : 7)));
        } else {
          setOffset((o) => Math.min(maxOffset, o + (view === "day" ? 1 : 7)));
        }
        return;
      }
      const a = rows[f.r - 1];
      if (a) {
        setDetailId(a.id);
        setMode("detail");
      }
    },
    onBack: () => setMode("list"),
  });

  // --- Detail focus ---
  // Normally ONE pill, so Back and OK both mean "go back". An activity that
  // carries an `action` (a film's "Spela") puts it NEXT TO Back in one row:
  // column 0 is the action, column 1 is Back. The action does nothing yet -
  // there is no playback behind it - so OK on it is deliberately a no-op, not
  // a Back.
  const openActivity = rows.find((a) => a.id === detailId);
  const hasAction = !!openActivity?.action;
  const [detailFocus, setDetailFocus] = useFocusGrid({
    rows: 1,
    cols: hasAction ? 2 : 1,
    scopeRef,
    wrap: false,
    enabled: mode === "detail",
    onEnter: (f) => {
      if (hasAction && f.c === 0) return;
      setMode("pane");
    },
    onBack: () => setMode("pane"),
  });
  // Each opened activity starts on its first row, not wherever the last left it.
  useEffect(() => {
    setDetailFocus({ r: 0, c: 0 });
  }, [detailId, setDetailFocus]);

  /**
   * Land on the first ACTIVITY when the remote enters the pane, not on the
   * Dag/Vecka strip.
   *
   * `useFocusGrid`'s `initial` is read once, on the first render - which here
   * is always before the fetch lands, so it can only ever say row 0. Without
   * this, OK from the menu put the ring on the "Dag" pill and the resident's
   * next OK re-selected the view they were already looking at: a press that
   * appears to do nothing.
   *
   * Only from "list": coming back out of a detail must return to the activity
   * that was open, not to the top of the list.
   */
  /**
   * Keep the focused row on screen.
   *
   * The list scrolls, and the only way a resident moves through it is the
   * arrow keys - so a focus ring that walks off the bottom edge leaves them
   * pressing DOWN at a screen that has stopped changing. `block: "nearest"`
   * so a row already visible is never yanked to the middle.
   */
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  useEffect(() => {
    if (mode !== "pane" || paneFocus.r === 0) return;
    const a = rows[paneFocus.r - 1];
    if (a) rowRefs.current[a.id]?.scrollIntoView({ block: "nearest" });
  }, [mode, paneFocus.r, rows]);

  const prevMode = useRef<VilmerMode>(mode);
  useEffect(() => {
    if (prevMode.current === "list" && mode === "pane") {
      setPaneFocus({ r: rows.length ? 1 : 0, c: 0 });
    }
    prevMode.current = mode;
  }, [mode, rows.length, setPaneFocus]);

  // Changing the view or the day can leave the focus index past the end of a
  // shorter list. Clamp instead of letting the ring vanish - a focus ring that
  // is simply not drawn anywhere is indistinguishable from a dead remote.
  useEffect(() => {
    if (mode !== "pane") return;
    if (paneFocus.r > rows.length) {
      setPaneFocus({ r: Math.max(0, rows.length), c: 0 });
    }
  }, [mode, rows.length, paneFocus.r, setPaneFocus]);

  const detail = rows.find((a) => a.id === detailId) ?? null;

  const label = (text: string, muted?: boolean) => (
    <div
      style={{
        fontFamily: p.mono,
        fontSize: 16,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: muted ? p.inkMuted : p.gCore,
      }}
    >
      {text}
    </div>
  );

  /**
   * The provider's own credit line.
   *
   * These are VilMer's activities, VilMer's photographs and VilMer's
   * descriptions - the screen says so, in their mark where they send one. The
   * logo comes from the FEED (`brand.logoUrl`), never from our bundle: pinning
   * their artwork in Foyer would keep serving a stale mark after they rebrand
   * and would make us the ones deciding how their brand looks in someone's
   * home. When they send no brand block we fall back to the word alone.
   */
  const brand = result?.brand;
  const providerLine = (right?: string) => (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      {label(right ?? "Aktiviteter")}
      <span
        style={{
          width: 1,
          height: 16,
          background: p.hairSoft,
          display: "inline-block",
        }}
      />
      {brand?.logoUrl && !logoFailed ? (
        <img
          src={brand.logoUrl}
          alt={brand.name ?? "VilMer"}
          // A logo is a wordmark at whatever aspect its owner drew it: fix the
          // HEIGHT and let the width follow, never the other way round.
          style={{ height: 26, width: "auto", display: "block" }}
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <span
          style={{
            fontFamily: p.mono,
            fontSize: 16,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            // Their colour when they state one, ours when they don't.
            color: brand?.color ?? p.inkMuted,
            fontWeight: 700,
          }}
        >
          {brand?.name ?? "VilMer"}
        </span>
      )}
    </div>
  );

  // ── Detail: one activity fills the pane ──────────────────────────────────
  if (mode === "detail" && detail) {
    const Glyph = vilmerGlyph(detail.category);
    const hero = imageOf(detail);
    return (
      <>
        {providerLine(detail.cancelled ? "Inställd" : "Aktivitet")}
        {/* The body SCROLLS and the Back pill sits outside it. A description
            may run to a paragraph, and once it did the pill was pushed out of
            an `overflow: hidden` pane - the resident could still press Back,
            but nothing on screen said so any more. */}
        <div
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          {hero && detail.action && (
            // A showpiece (a film): its art is KEY ART - a portrait and a logo
            // in a roughly 3:2 frame - and a 6:1 band cut with `cover` slices
            // through the logo. So the band is black and the whole picture sits
            // at its right edge, uncropped, fading into the black on the left.
            <div
              style={{
                width: "100%",
                height: 210,
                flex: "0 0 auto",
                borderRadius: 20,
                background: "#000",
                display: "flex",
                justifyContent: "flex-end",
                overflow: "hidden",
                opacity: detail.cancelled ? 0.45 : 1,
              }}
            >
              {detail.banner && (
                // The film's own title card, in the black part of the band.
                <div
                  data-testid="vilmer-banner"
                  style={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    padding: "0 24px 0 32px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 8,
                    color: "#fff",
                  }}
                >
                  <div
                    style={{
                      fontSize: 44,
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                      lineHeight: 1.1,
                    }}
                  >
                    {detail.banner.title}
                  </div>
                  {detail.banner.meta && (
                    <div
                      style={{ fontSize: 20, color: "rgba(255,255,255,0.6)" }}
                    >
                      {detail.banner.meta}
                    </div>
                  )}
                  {detail.banner.text && (
                    <div
                      style={{
                        fontSize: 22,
                        lineHeight: 1.35,
                        marginTop: 6,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {detail.banner.text}
                    </div>
                  )}
                </div>
              )}
              <div
                style={{
                  position: "relative",
                  height: "100%",
                  flex: "0 0 auto",
                  marginLeft: "auto",
                }}
              >
                <img
                  src={hero}
                  alt=""
                  onError={() => markBadImage(detail.id)}
                  style={{ height: "100%", width: "auto", display: "block" }}
                />
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(to right, #000 0%, rgba(0,0,0,0) 35%)",
                  }}
                />
              </div>
            </div>
          )}
          {hero && !detail.action && (
            <img
              src={hero}
              alt=""
              onError={() => markBadImage(detail.id)}
              style={{
                width: "100%",
                // A fixed BAND, cropped - an activity photo arrives at whatever
                // aspect ratio someone's phone took it at, and letting it size
                // itself pushes the description off a 1080p pane.
                //
                // 260 -> 200 -> 130. The card is WIDE and SHORT (~620px of
                // usable height) and everything stacked in it competes for the
                // same scarce vertical space. At 200 the description was
                // squeezed to a single clipped sliver. The text is the point of
                // this card; the picture yields to it.
                height: 130,
                objectFit: "cover",
                borderRadius: 20,
                display: "block",
                background: p.surface2,
                // Cancelled: the photo stays (it is still the activity) but
                // steps back, so the struck-through title reads first.
                opacity: detail.cancelled ? 0.45 : 1,
              }}
            />
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <span style={{ color: p.gCore, display: "inline-flex" }}>
              <Glyph size={44} />
            </span>
            <h2
              style={{
                // 64 bought one line of title at the cost of two lines of
                // description, on a card where height is the scarce axis.
                fontSize: 52,
                fontWeight: 700,
                letterSpacing: "-0.03em",
                // >=1.2 - the pane clips overflow, and below that descenders go.
                lineHeight: 1.2,
                margin: 0,
                color: p.ink,
                textDecoration: detail.cancelled ? "line-through" : "none",
              }}
            >
              {detail.title}
            </h2>
          </div>
          <div style={{ fontSize: 32, color: p.ink, fontWeight: 600 }}>
            {vilmerDayLabel(activityDate(detail), today)}
            {activityTimeRange(detail) ? ` · ${activityTimeRange(detail)}` : ""}
            {detail.location ? ` · ${detail.location}` : ""}
          </div>
          {detail.cancelled && (
            <div style={{ fontSize: 28, color: p.gDeep, fontWeight: 700 }}>
              Den här aktiviteten är inställd.
            </div>
          )}
          {detail.description && !detail.banner && (
            <p
              style={{
                fontSize: 30,
                lineHeight: 1.45,
                color: p.inkSoft,
                margin: 0,
                // Full card width, on purpose, against the usual 45-75ch reading
                // measure. That rule is for pages of prose; this is one or two
                // sentences on a card with nothing else competing for the space,
                // and capping it left half the card visibly empty - which reads
                // as a layout mistake rather than as considered typography.
                width: "100%",
              }}
            >
              {detail.description}
            </p>
          )}
        </div>
        {detail.action ? (
          // Spela and Back share one row. A slim ring (not the 14px home-tile
          // one) keeps two small buttons from reading as two big blobs.
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flex: "0 0 auto",
            }}
          >
            <div
              data-testid="vilmer-action"
              style={{
                padding: "14px 38px",
                borderRadius: 999,
                background: p.gCore,
                color: "#fff",
                fontSize: 26,
                fontWeight: 700,
                boxShadow: detailFocus.c === 0 ? BUTTON_FOCUS(p) : "none",
              }}
            >
              ▶ {detail.action.label}
            </div>
            <div
              style={{
                padding: "14px 28px",
                borderRadius: 999,
                background: p.surface2,
                color: p.ink,
                fontSize: 22,
                fontWeight: 600,
                boxShadow: detailFocus.c === 1 ? BUTTON_FOCUS(p) : "none",
              }}
            >
              ◀ Tillbaka
            </div>
          </div>
        ) : (
          <div
            style={{
              alignSelf: "flex-start",
              flex: "0 0 auto",
              padding: "16px 28px",
              borderRadius: 999,
              background: p.gDeep,
              color: "#fff",
              fontSize: 24,
              fontWeight: 700,
            }}
          >
            ◀ Tillbaka till aktiviteter
          </div>
        )}
      </>
    );
  }

  // ── Not wired up / nothing to show ───────────────────────────────────────
  const notice =
    result?.unconfigured === true
      ? "Aktiviteterna är inte kopplade än. Personalen ordnar det."
      : failed
        ? "Vi når inte aktivitetsprogrammet just nu. Försök igen om en stund."
        : loading
          ? "Hämtar aktiviteter …"
          : null;

  const controls: { key: string; text: string; active: boolean }[] = [
    { key: "day", text: "Dag", active: view === "day" },
    { key: "week", text: "Vecka", active: view === "week" },
    { key: "prev", text: "‹", active: false },
    { key: "next", text: "›", active: false },
  ];

  return (
    <>
      {providerLine(result?.stale ? "Senast hämtade" : "Aktiviteter")}

      {/* Control strip. Focusable only once the remote is inside the pane;
          until then it is a legend telling you what OK will give you. */}
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        {controls.map((c, i) => {
          const focused =
            mode === "pane" && paneFocus.r === 0 && paneFocus.c === i;
          const disabled =
            (c.key === "prev" && offset === 0) ||
            (c.key === "next" && offset >= maxOffset);
          return (
            <div
              key={c.key}
              style={{
                padding:
                  c.key === "prev" || c.key === "next"
                    ? "10px 20px"
                    : "10px 26px",
                borderRadius: 999,
                fontSize: 24,
                fontWeight: 700,
                border: `2px solid ${focused ? p.gCore : "transparent"}`,
                background: focused
                  ? p.gDeep
                  : c.active
                    ? p.gTint
                    : "transparent",
                color: focused
                  ? "#fff"
                  : disabled
                    ? p.inkMuted
                    : c.active
                      ? p.gDeep
                      : p.inkSoft,
                opacity: disabled && !focused ? 0.45 : 1,
              }}
            >
              {c.text}
            </div>
          );
        })}
        <div style={{ flex: "1 1 auto" }} />
        <div style={{ fontSize: 24, color: p.inkSoft, fontWeight: 600 }}>
          {view === "day"
            ? vilmerDayLabel(dayIso, today)
            : `${vilmerDayLabel(days[0] ?? dayIso, today)} – ${vilmerDayLabel(
                days[days.length - 1] ?? dayIso,
                today
              )}`}
        </div>
      </div>

      {notice ? (
        <div
          style={{
            flex: "1 1 auto",
            display: "flex",
            alignItems: "center",
            fontSize: 30,
            color: p.inkSoft,
          }}
        >
          {notice}
        </div>
      ) : rows.length === 0 ? (
        <div
          style={{
            flex: "1 1 auto",
            display: "flex",
            alignItems: "center",
            fontSize: 30,
            color: p.inkSoft,
          }}
        >
          {view === "day"
            ? "Inga aktiviteter den här dagen."
            : "Inga aktiviteter de närmaste dagarna."}
        </div>
      ) : (
        <div
          style={{
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            // SCROLL, never clip. A week can hold more rows than the pane, and
            // `overflow: hidden` cut the last one in half with nothing on
            // screen admitting it - the resident arrows down onto a row they
            // cannot see. The focused row is scrolled into view below.
            overflowY: "auto",
            // Full pane width. This was capped at 1080px so a row stayed inside
            // one glance, but the cap left a fifth of the card empty and the
            // focused row visibly short of its own edge - and clipped longer
            // location names. The location column is wider instead.
          }}
        >
          {days.map((d) => {
            const dayRows = activitiesOn(activities, d);
            if (view === "week" && dayRows.length === 0) return null;
            return (
              <div key={d}>
                {view === "week" && (
                  <div
                    style={{
                      fontFamily: p.mono,
                      fontSize: 15,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: p.inkMuted,
                      margin: "6px 0 4px",
                    }}
                  >
                    {vilmerDayLabel(d, today)}
                  </div>
                )}
                {dayRows.map((a) => {
                  const idx = rows.indexOf(a);
                  const focused = mode === "pane" && paneFocus.r === idx + 1;
                  const done = isFinished(a, now);
                  const Glyph = vilmerGlyph(a.category);
                  const thumb = imageOf(a);
                  return (
                    <div
                      key={a.id}
                      ref={(el) => {
                        rowRefs.current[a.id] = el;
                      }}
                      style={{
                        display: "grid",
                        // The picture column is a fixed width whether it holds
                        // a photo or the fallback glyph, so rows with and
                        // without artwork keep their titles on one vertical
                        // line. Sized for a 55-inch panel at three metres: at
                        // 64px the artwork was there and read as an empty box.
                        gridTemplateColumns: "104px 104px 1fr 320px",
                        alignItems: "center",
                        gap: 16,
                        padding: "10px 18px",
                        borderRadius: 18,
                        background: focused ? p.gDeep : "transparent",
                        color: focused ? "#fff" : p.ink,
                        // Finished and cancelled both fade, but only cancelled
                        // is struck through - "you missed it" and "it is not
                        // happening" are different messages.
                        opacity: done && !focused ? 0.5 : 1,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: p.mono,
                          fontSize: 26,
                          fontWeight: 700,
                          color: focused ? p.gGlow : p.gCore,
                        }}
                      >
                        {a.allDay ? "Heldag" : activityTimeRange(a).slice(0, 5)}
                      </span>
                      {thumb ? (
                        <img
                          src={thumb}
                          alt=""
                          onError={() => markBadImage(a.id)}
                          style={{
                            width: 104,
                            height: 72,
                            objectFit: "cover",
                            borderRadius: 12,
                            display: "block",
                            background: p.surface2,
                          }}
                        />
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            justifyContent: "center",
                            color: focused ? p.gGlow : p.gSoft,
                          }}
                        >
                          <Glyph size={26} />
                        </span>
                      )}
                      <span
                        style={{
                          fontSize: 28,
                          fontWeight: 600,
                          textDecoration: a.cancelled ? "line-through" : "none",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {a.title}
                        {a.cancelled ? " · inställd" : ""}
                      </span>
                      <span
                        style={{
                          fontSize: 22,
                          color: focused ? p.gGlow : p.inkMuted,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {done && !a.cancelled ? "Avslutad" : (a.location ?? "")}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ── INFO ────────────────────────────────────────────────────────────────

function Info({ go, scopeRef, context }: ScreenProps) {
  const p = useC();
  const scheme = useScheme();
  const sections = scheme.content.infoSections;
  // Secondary screens (Wi-Fi / Välkommen / Instagram / Väder) are listed here
  // as navigable links beneath the info sections - selecting one opens it.
  const secondary = visibleSecondarySections(scheme);
  /**
   * VilMer renders INSIDE this pane rather than opening a screen of its own, so
   * the remote can be in one of two places. While it is inside the pane the
   * list grid below is disabled - `useFocusGrid` binds a window-level listener
   * per grid, and two live grids would both act on the same key press.
   */
  const [vilmerMode, setVilmerMode] = useState<VilmerMode>("list");
  // Row 0 is the Tillbaka pill; sections then secondary links follow.
  const [focus] = useFocusGrid({
    rows: sections.length + secondary.length + 1,
    cols: 1,
    scopeRef,
    initial: { r: 1, c: 0 },
    wrap: true,
    enabled: vilmerMode === "list",
    onEnter: (f) => {
      if (f.r === 0) {
        go("home");
        return;
      }
      const idx = f.r - 1;
      const link = secondary[idx - sections.length];
      // VilMer is the one secondary that does not navigate: OK hands the
      // remote to the pane that is already showing its activities.
      if (link === "vilmer") {
        setVilmerMode("pane");
        return;
      }
      if (link) go(link as Screen);
    },
    onBack: () => go("home"),
  });
  const backFocused = focus.r === 0;
  const navIdx = backFocused ? 0 : focus.r - 1;
  const onSecondary = !backFocused && navIdx >= sections.length;
  const sectionIdx = Math.min(navIdx, Math.max(0, sections.length - 1));
  const sec = sections[sectionIdx] ?? sections[0];
  const focusedSecondary = onSecondary
    ? secondary[navIdx - sections.length]
    : undefined;
  const onVilmer = focusedSecondary === "vilmer";
  const now = useNow();
  // Embedded activity scheme (the legacy iframe path): substitute the paired
  // room's token. Kept as a generic escape hatch - VilMer no longer needs it.
  const embedSrc = sec?.embedUrl
    ? sec.embedUrl.replace(
        /\{integrationId\}/g,
        encodeURIComponent(context.integrationId ?? "")
      )
    : undefined;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main style={subMain}>
        <SubTitle
          eyebrow="04 · Info"
          title="Information"
          right={`Uppdaterad ${formatTime(now)}`}
          backFocused={backFocused}
        />
        <div
          style={{
            flex: "1 1 auto",
            display: "grid",
            gridTemplateColumns: "380px 1fr",
            gap: 32,
            minHeight: 0,
          }}
        >
          {/* Nav */}
          <nav
            style={{
              background: p.surface,
              border: `1px solid ${p.hairSoft}`,
              borderRadius: 32,
              padding: 24,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {sections.map((s, i) => {
              const focused = !backFocused && !onSecondary && i === navIdx;
              return (
                <div
                  key={s.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "36px 1fr 18px",
                    alignItems: "center",
                    gap: 16,
                    padding: "22px 22px",
                    borderRadius: 22,
                    background: focused ? p.gDeep : "transparent",
                    color: focused ? "#fff" : p.inkSoft,
                    fontSize: 24,
                    fontWeight: 600,
                  }}
                >
                  <span
                    style={{
                      fontFamily: p.mono,
                      fontSize: 16,
                      color: focused ? p.gGlow : p.inkMuted,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{s.section}</span>
                  <span
                    style={{
                      opacity: focused ? 1 : 0.4,
                      display: "inline-flex",
                    }}
                  >
                    <Icon.Chevron size={18} />
                  </span>
                </div>
              );
            })}
            {secondary.map((id, j) => {
              const focused = !backFocused && navIdx === sections.length + j;
              const Glyph = GLYPH[id] ?? Icon.Info;
              return (
                <div
                  key={id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "36px 1fr 18px",
                    alignItems: "center",
                    gap: 16,
                    padding: "22px 22px",
                    borderRadius: 22,
                    background: focused ? p.gDeep : "transparent",
                    color: focused ? "#fff" : p.inkSoft,
                    fontSize: 24,
                    fontWeight: 600,
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      color: focused ? p.gGlow : p.gCore,
                    }}
                  >
                    <Glyph size={22} />
                  </span>
                  <span>{DEST[id]?.label ?? id}</span>
                  <span
                    style={{
                      opacity: focused ? 1 : 0.4,
                      display: "inline-flex",
                    }}
                  >
                    <Icon.Chevron size={18} />
                  </span>
                </div>
              );
            })}
          </nav>

          {/* Content */}
          <section
            style={{
              background: p.surface,
              border: `1px solid ${p.hairSoft}`,
              borderRadius: 32,
              padding: "48px 56px",
              display: "flex",
              flexDirection: "column",
              gap: 28,
              overflow: "hidden",
            }}
          >
            {onVilmer ? (
              <VilmerPane
                mode={vilmerMode}
                setMode={setVilmerMode}
                scopeRef={scopeRef}
                context={context}
              />
            ) : onSecondary ? (
              <div
                style={{
                  flex: "1 1 auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  gap: 18,
                }}
              >
                <div
                  style={{
                    fontFamily: p.mono,
                    fontSize: 16,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: p.gCore,
                  }}
                >
                  {DEST[secondary[navIdx - sections.length]]?.kicker}
                </div>
                <h2
                  style={{
                    fontSize: 84,
                    fontWeight: 700,
                    letterSpacing: "-0.035em",
                    // >=1.2 - free-text label inside this section's
                    // overflow:hidden box; below that, descenders clip.
                    lineHeight: 1.2,
                    margin: 0,
                    color: p.ink,
                  }}
                >
                  {DEST[secondary[navIdx - sections.length]]?.label}
                </h2>
                <p style={{ fontSize: 28, color: p.inkSoft, margin: 0 }}>
                  Tryck <strong>OK</strong> för att öppna.
                </p>
              </div>
            ) : (
              <>
                <div>
                  <div
                    style={{
                      fontFamily: p.mono,
                      fontSize: 16,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: p.gCore,
                    }}
                  >
                    {sec?.section} · {careDate(now)}
                  </div>
                  {!embedSrc && (
                    <h2
                      style={{
                        fontSize: 84,
                        fontWeight: 700,
                        letterSpacing: "-0.035em",
                        // >=1.2 - same overflow:hidden descender-clipping
                        // fix as the sibling h2 above.
                        lineHeight: 1.2,
                        margin: "10px 0 0",
                        color: p.ink,
                      }}
                    >
                      {sec?.items[0]?.value ?? sec?.section}
                    </h2>
                  )}
                </div>
                {embedSrc ? (
                  <iframe
                    title={sec?.section}
                    src={embedSrc}
                    style={{
                      flex: "1 1 auto",
                      width: "100%",
                      border: "none",
                      borderRadius: 16,
                      background: p.surface2,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 28,
                    }}
                  >
                    {sec?.items.map((it, i) => (
                      <article
                        key={i}
                        style={{
                          background: p.paper,
                          borderRadius: 22,
                          padding: "32px 32px 28px",
                          display: "flex",
                          flexDirection: "column",
                          gap: 12,
                          border: `1px solid ${p.hairSoft}`,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: p.mono,
                            fontSize: 16,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: p.gCore,
                            fontWeight: 700,
                          }}
                        >
                          {it.label}
                        </span>
                        <span
                          style={{
                            fontSize: 30,
                            fontWeight: 700,
                            letterSpacing: "-0.02em",
                            lineHeight: 1.1,
                            color: p.ink,
                          }}
                        >
                          {it.value}
                        </span>
                      </article>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </main>
      {/* The hint follows where the remote actually IS. Handing focus into the
          pane without saying how to get out is the one thing that makes a
          two-pane screen feel like a trap. */}
      <Hint
        keys={
          vilmerMode === "detail"
            ? [{ cap: "◀", label: "Tillbaka till aktiviteter" }]
            : vilmerMode === "pane"
              ? [
                  { cap: "▲▼", label: "Välj aktivitet" },
                  { cap: "OK", label: "Läs mer" },
                  { cap: "◀", label: "Tillbaka till menyn" },
                ]
              : [
                  { cap: "▲▼", label: "Bläddra meny" },
                  { cap: "OK", label: onVilmer ? "Välj aktivitet" : "Visa" },
                  { cap: "◀", label: "Tillbaka" },
                ]
        }
      />
    </div>
  );
}

// ── Shared sub-page bits (care language) ────────────────────────────────────

/**
 * Where this screen's Back goes, following the premise's PLACEMENT of it: a
 * screen listed inside Information returns to Information, a home tile returns
 * home. Placement is now an operator choice (Screens board), so this can no
 * longer be a constant per screen - Matsedel promoted to a home tile whose Back
 * still dropped the resident into Information would be a trapdoor.
 */
function useBackTarget(id: string): Screen {
  return screenBackTarget(useScheme(), id) as Screen;
}

/** Focus for a display-only sub-page: a single focusable Tillbaka (OK / ◀
 *  return home). Returns whether Back is focused (always true here). */
function useBackOnly(
  go: (s: Screen) => void,
  scopeRef: RefObject<HTMLDivElement>,
  target: Screen = "home"
) {
  const [focus] = useFocusGrid({
    rows: 1,
    cols: 1,
    scopeRef,
    onEnter: () => go(target),
    onBack: () => go(target),
  });
  return focus.r === 0;
}

/**
 * The premise's own page, on the care launcher.
 *
 * Care supplies nothing but the bar's chrome: this screen is FULL-BLEED by
 * design (no `Head`, no `SubTitle`), because the operator's picture is the
 * page. A header strip here would crop the very thing they uploaded, and the
 * item bar already tells the resident where they are. Back returns wherever the
 * premise placed the screen, exactly like every other sub-page.
 */
function CareCustom({ go, scopeRef }: ScreenProps) {
  const p = useC();
  const back = useBackTarget("custom");
  return (
    <CustomScreen
      scopeRef={scopeRef}
      onBack={() => go(back)}
      style={{
        barBg: "rgba(23,25,21,0.55)",
        ink: p.paper,
        accent: p.gCore,
        onAccent: p.paper,
        font: p.sans,
      }}
    />
  );
}

const careCard: CSSProperties = { borderRadius: 32 };

function Label({ children }: { children: ReactNode }) {
  const p = useC();
  return (
    <div
      style={{
        fontFamily: p.mono,
        fontSize: 15,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: p.inkMuted,
      }}
    >
      {children}
    </div>
  );
}

// ── Wi-Fi ───────────────────────────────────────────────────────────────────

function CareWifi({ go, scopeRef, context }: ScreenProps) {
  const p = useC();
  const wifi = useScheme().content.wifi;
  const backFocused = useBackOnly(go, scopeRef, useBackTarget("wifi"));
  const qr = wifi
    ? `WIFI:T:${wifi.password ? "WPA" : "nopass"};S:${wifi.ssid};${
        wifi.password ? `P:${wifi.password};` : ""
      };`
    : "";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main style={subMain}>
        <SubTitle eyebrow="Anslut" title="Wi-Fi" backFocused={backFocused} />
        <div style={{ flex: 1, display: "flex", gap: 36, minHeight: 0 }}>
          <div
            style={{
              ...careCard,
              background: p.surface,
              border: `1px solid ${p.hairSoft}`,
              flex: 1,
              padding: "52px 60px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 36,
            }}
          >
            <div>
              <Label>Nätverk</Label>
              <div
                style={{
                  fontSize: 64,
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  color: p.ink,
                  marginTop: 8,
                }}
              >
                {wifi?.ssid}
              </div>
            </div>
            {wifi?.password && (
              <div>
                <Label>Lösenord</Label>
                <div
                  style={{
                    fontFamily: p.mono,
                    fontSize: 48,
                    color: p.ink,
                    marginTop: 8,
                  }}
                >
                  {wifi.password}
                </div>
              </div>
            )}
          </div>
          {wifi?.showQr && (
            <div style={{ display: "grid", placeItems: "center" }}>
              <div
                style={{ background: "#fff", padding: 28, borderRadius: 28 }}
              >
                <QRCode
                  value={qr}
                  size={280}
                  bgColor="#fff"
                  fgColor={p.gDeep}
                />
              </div>
            </div>
          )}
        </div>
      </main>
      <Hint keys={[{ cap: "◀", label: "Tillbaka" }]} />
    </div>
  );
}

// ── Välkommen ────────────────────────────────────────────────────────────────

function CareWelcome({ go, scopeRef, context }: ScreenProps) {
  const p = useC();
  const w = useScheme().content.welcome;
  const backFocused = useBackOnly(go, scopeRef, useBackTarget("welcome"));
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main style={subMain}>
        <SubTitle
          eyebrow="Hälsning"
          title={w?.title || "Välkommen"}
          backFocused={backFocused}
        />
        <div
          style={{
            ...careCard,
            background: p.surface,
            border: `1px solid ${p.hairSoft}`,
            flex: 1,
            padding: "56px 64px",
            fontSize: 38,
            lineHeight: 1.5,
            color: p.ink,
            whiteSpace: "pre-wrap",
            overflow: "auto",
          }}
          className="foyer-scheme-scroll"
        >
          {w?.body}
        </div>
      </main>
      <Hint keys={[{ cap: "◀", label: "Tillbaka" }]} />
    </div>
  );
}

// ── Anhöriga (relatives) ─────────────────────────────────────────────────────

function CareRelatives({ go, scopeRef, context }: ScreenProps) {
  const p = useC();
  const rel = useScheme().content.relatives;
  const sections = rel?.sections ?? [];
  const embedUrl = rel?.embedUrl;
  const backFocused = useBackOnly(go, scopeRef, useBackTarget("relatives"));
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main style={subMain}>
        <SubTitle
          eyebrow="För familjen"
          title="Anhöriga"
          backFocused={backFocused}
        />
        {embedUrl ? (
          <iframe
            title="Anhöriga"
            src={embedUrl}
            style={{
              flex: 1,
              width: "100%",
              border: "none",
              borderRadius: 32,
              background: p.surface2,
            }}
          />
        ) : (
          <div
            className="foyer-scheme-scroll"
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridAutoRows: "min-content",
              gap: 28,
              overflow: "auto",
              minHeight: 0,
              alignContent: "start",
            }}
          >
            {sections.map((sec) => (
              <article
                key={sec.id}
                style={{
                  background: p.surface,
                  border: `1px solid ${p.hairSoft}`,
                  borderRadius: 28,
                  padding: "36px 40px",
                }}
              >
                <h2
                  style={{
                    fontSize: 38,
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    color: p.ink,
                    margin: "0 0 18px",
                  }}
                >
                  {sec.section}
                </h2>
                {sec.items.map((it, j) => (
                  <div
                    key={j}
                    style={{
                      fontSize: 26,
                      color: p.inkSoft,
                      marginTop: 10,
                      lineHeight: 1.35,
                    }}
                  >
                    <span style={{ color: p.ink, fontWeight: 600 }}>
                      {it.label}
                    </span>
                    {it.value ? ` - ${it.value}` : ""}
                  </div>
                ))}
              </article>
            ))}
          </div>
        )}
      </main>
      <Hint keys={[{ cap: "◀", label: "Tillbaka" }]} />
    </div>
  );
}

// ── Instagram ────────────────────────────────────────────────────────────────

/** Columns for the feed grid - scale with the post count so it stays balanced. */
function careIgCols(n: number, max = 4): number {
  if (n <= 1) return 1;
  if (n <= 4) return 2;
  if (n <= 9) return 3;
  return Math.min(4, max);
}

function CareInstagram({ go, scopeRef, context }: ScreenProps) {
  const p = useC();
  const ig = useScheme().content.instagram;
  const backFocused = useBackOnly(go, scopeRef, useBackTarget("instagram"));
  const handle = ig?.handle?.replace(/^@/, "") ?? "";
  // Legacy back-compat: configs without `display` are pure "invite" screens.
  const display = ig?.display ?? "invite";
  const wantFeed = display !== "invite";
  const wantQr = display !== "feed";
  const feedCount = ig?.feedCount ?? 6;
  const { posts } = useInstagramMedia(context.premiseId, feedCount, wantFeed);
  const hasFeed = wantFeed && posts.length > 0;
  // Graceful fallback: a feed that's requested but unavailable (not connected
  // yet, or upstream hiccup) drops back to the follow-us invite card.
  const mode: "feed" | "invite" | "both" = hasFeed ? display : "invite";
  const showQr = mode === "both" || (mode === "invite" && wantQr);

  const grid = (cols: number, posts2: InstagramPost[]) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: 18,
        flex: 1,
        alignContent: "start",
        minHeight: 0,
      }}
    >
      {posts2.map((post) => (
        <div
          key={post.id}
          style={{
            position: "relative",
            aspectRatio: "1 / 1",
            borderRadius: 24,
            overflow: "hidden",
            background: p.surface,
            border: `1px solid ${p.hairSoft}`,
          }}
        >
          <img
            src={post.thumbnailUrl ?? post.mediaUrl}
            alt=""
            loading="lazy"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </div>
      ))}
    </div>
  );

  const qrBlock = (
    <div style={{ display: "grid", placeItems: "center" }}>
      <div style={{ background: "#fff", padding: 28, borderRadius: 28 }}>
        <QRCode
          value={`https://instagram.com/${handle}`}
          size={280}
          bgColor="#fff"
          fgColor={p.gDeep}
        />
      </div>
    </div>
  );

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main style={subMain}>
        <SubTitle
          eyebrow="Följ oss"
          title="Instagram"
          backFocused={backFocused}
        />
        {mode === "feed" ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 24,
              minHeight: 0,
            }}
          >
            <div style={{ fontFamily: p.mono, fontSize: 26, color: p.inkSoft }}>
              @{handle} · instagram.com/{handle}
            </div>
            {grid(careIgCols(posts.length), posts)}
          </div>
        ) : mode === "both" ? (
          <div style={{ flex: 1, display: "flex", gap: 36, minHeight: 0 }}>
            {grid(careIgCols(posts.length, 3), posts)}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 24,
              }}
            >
              {qrBlock}
              <div
                style={{ fontFamily: p.mono, fontSize: 24, color: p.inkSoft }}
              >
                @{handle}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", gap: 36, minHeight: 0 }}>
            <div
              style={{
                background: p.surface,
                border: `1px solid ${p.hairSoft}`,
                borderRadius: 32,
                flex: 1,
                padding: "56px 64px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 24,
              }}
            >
              {ig?.caption && (
                <div
                  style={{ fontSize: 32, lineHeight: 1.4, color: p.inkSoft }}
                >
                  {ig.caption}
                </div>
              )}
              <div>
                <Label>Profil</Label>
                <div
                  style={{
                    fontSize: 64,
                    fontWeight: 700,
                    letterSpacing: "-0.03em",
                    color: p.ink,
                    marginTop: 8,
                  }}
                >
                  @{handle}
                </div>
                <div
                  style={{
                    fontFamily: p.mono,
                    fontSize: 26,
                    color: p.inkSoft,
                    marginTop: 12,
                  }}
                >
                  instagram.com/{handle}
                </div>
              </div>
            </div>
            {showQr && qrBlock}
          </div>
        )}
      </main>
      <Hint keys={[{ cap: "◀", label: "Tillbaka" }]} />
    </div>
  );
}

// ── Matsedel (food menu) ────────────────────────────────────────────────────

/**
 * The week's meals, Monday→Sunday, headed by the week number.
 *
 * Navigation is deliberately arrow-only: moving left/right across the day strip
 * changes the panel underneath *immediately*, with no OK press. A resident
 * browsing the week should never have to learn a select-then-confirm ritual,
 * and there is nothing here to "open" - the day IS the content.
 *
 * Today is pre-focused and stays marked while the resident wanders, so it is
 * always obvious which column is now.
 */
function CareMenu({ go, scopeRef, context }: ScreenProps) {
  const p = useC();
  const scheme = useScheme();
  const menu = scheme.content.menu;
  const now = useNow();
  const locale = context.locale ?? "sv";
  const backTo = useBackTarget("menu");

  // Feed-backed menus fetch; manual ones never touch the network.
  const wantsFeed = menu?.source === "feed" && menu.hasFeed;
  const { result, loading, failed } = useMenuFeed(wantsFeed, context.premiseId);

  // Which weeks are we rendering? The live feed wins when it actually returned
  // something; otherwise we fall back to the stored weeks, so a kitchen server
  // that went down shows last-known-good instead of an empty screen.
  const feedWeeks: ResolvedMenuWeek[] = (result?.weeks ?? []).map((w) => ({
    year: w.year,
    week: w.week,
    days: w.days.map((d) => ({
      date: d.date,
      meals: d.meals.map((m) => ({
        id: m.id,
        label: resolveLocalized(m.label, locale, locale),
        body: resolveLocalized(m.body, locale, locale),
        note:
          m.note == null ? undefined : resolveLocalized(m.note, locale, locale),
      })),
    })),
  }));
  const weeks: ResolvedMenuWeek[] =
    wantsFeed && feedWeeks.length ? feedWeeks : (menu?.weeks ?? []);

  // Which ISO week is on screen. Starts at the week the premise is living in,
  // then steps with the ◀ / ▶ pills.
  const today = toIsoDate(now);
  const nowParts = isoWeekParts(now);
  const [offset, setOffset] = useState(0);
  const shown = shiftIsoWeek(nowParts.year, nowParts.week, offset);
  const dates = isoWeekDates(shown.year, shown.week);
  const week = weeks.find(
    (w) => w.year === shown.year && w.week === shown.week
  );

  // Only offer a step in a direction that has an authored week - a resident
  // paging into six empty weeks learns nothing and gets lost.
  const hasWeek = (delta: number) => {
    const t = shiftIsoWeek(nowParts.year, nowParts.week, offset + delta);
    return weeks.some(
      (w) =>
        w.year === t.year &&
        w.week === t.week &&
        w.days.some((d) => d.meals.length > 0)
    );
  };
  const canPrev = hasWeek(-1);
  const canNext = hasWeek(1);

  // Row 0 controls, left to right. Back is always first so ← lands somewhere
  // predictable; the week steps only appear when they lead anywhere.
  const controls: Array<{ kind: "back" | "prev" | "next"; label: string }> = [
    { kind: "back", label: "Tillbaka" },
    ...(canPrev
      ? [
          {
            kind: "prev" as const,
            label: `Vecka ${shiftIsoWeek(shown.year, shown.week, -1).week}`,
          },
        ]
      : []),
    ...(canNext
      ? [
          {
            kind: "next" as const,
            label: `Vecka ${shiftIsoWeek(shown.year, shown.week, 1).week}`,
          },
        ]
      : []),
  ];

  // Start on today when today is on screen, else on Monday.
  const todayCol = dates.indexOf(today);
  const [focus] = useFocusGrid({
    rows: 2,
    cols: 7,
    initial: { r: 1, c: todayCol >= 0 ? todayCol : 0 },
    scopeRef,
    wrap: false,
    // Back returns wherever the premise PUT Matsedel: Information when it is
    // listed there (the default - same as Wi-Fi and Väder), Home when an
    // operator promoted it to a tile. Jumping the resident out to a screen they
    // did not arrive from would lose their place in the list.
    onBack: () => go(backTo),
    onEnter: (f) => {
      if (f.r !== 0) return;
      const ctl = controls[Math.min(f.c, controls.length - 1)];
      if (ctl.kind === "back") go(backTo);
      if (ctl.kind === "prev") setOffset((o) => o - 1);
      if (ctl.kind === "next") setOffset((o) => o + 1);
    },
  });

  /**
   * Which day the panel shows. Kept separate from the focus cell so that
   * moving UP to the week controls does not blank or re-point the panel - the
   * resident keeps reading Wednesday's lunch while they reach for "next week".
   */
  const [dayCol, setDayCol] = useState(todayCol >= 0 ? todayCol : 0);
  useEffect(() => {
    if (focus.r === 1) setDayCol(focus.c);
  }, [focus.r, focus.c]);
  // A new week re-anchors on today when today is in it, else on Monday, so
  // paging never leaves the panel pointed at a date that is no longer shown.
  useEffect(() => {
    const col = dates.indexOf(today);
    setDayCol(col >= 0 ? col : 0);
    // Only when the displayed week changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const selectedDate = dates[Math.min(dayCol, 6)];
  const day = week?.days.find((d) => d.date === selectedDate);
  const meals = day?.meals ?? [];

  const monday = fromIsoDate(dates[0]);
  const sunday = fromIsoDate(dates[6]);
  const span =
    monday && sunday
      ? `${monday.getDate()} ${MONTHS_SV[monday.getMonth()]} - ${sunday.getDate()} ${MONTHS_SV[sunday.getMonth()]}`
      : "";

  const emptyCard = (text: string) => (
    <div
      style={{
        ...careCard,
        background: p.surface,
        border: `1px solid ${p.hairSoft}`,
        flex: 1,
        display: "grid",
        placeItems: "center",
        padding: "56px 64px",
        fontSize: 34,
        color: p.inkSoft,
        textAlign: "center",
      }}
    >
      {text}
    </div>
  );

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main style={subMain}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 24,
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: 28 }}>
            <BackPill focused={focus.r === 0 && focus.c === 0} />
            <div>
              <div
                style={{
                  fontFamily: p.mono,
                  fontSize: 15,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: p.inkMuted,
                }}
              >
                Vecka {shown.week} · {span}
              </div>
              <h1
                style={{
                  fontSize: 76,
                  fontWeight: 700,
                  letterSpacing: "-0.035em",
                  lineHeight: 1.1,
                  color: p.ink,
                  margin: "4px 0 0",
                }}
              >
                Matsedel
              </h1>
            </div>
          </div>
          {controls.length > 1 && (
            <div style={{ display: "flex", gap: 14 }}>
              {controls.slice(1).map((ctl, i) => {
                // +1: control 0 is the Back pill rendered on the left.
                const focused =
                  focus.r === 0 &&
                  Math.min(focus.c, controls.length - 1) === i + 1;
                return (
                  <span
                    key={ctl.kind}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "14px 24px",
                      borderRadius: 999,
                      background: focused ? p.gCore : p.surface,
                      border: `1px solid ${focused ? p.gCore : p.hairSoft}`,
                      color: focused ? "#fff" : p.inkSoft,
                      fontSize: 22,
                      fontWeight: 600,
                      boxShadow: focused
                        ? `0 0 0 4px ${p.paper}, 0 0 0 10px ${p.gCore}`
                        : "none",
                    }}
                  >
                    {ctl.kind === "prev" ? "◀" : ""} {ctl.label}{" "}
                    {ctl.kind === "next" ? "▶" : ""}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Day strip - Monday to Sunday, the week at a glance. */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 14,
            flex: "0 0 auto",
          }}
        >
          {dates.map((date, i) => {
            const d = fromIsoDate(date);
            const dayMeals =
              week?.days.find((x) => x.date === date)?.meals ?? [];
            const isToday = date === today;
            const focused = focus.r === 1 && focus.c === i;
            // Selected-but-not-focused keeps the panel's day identifiable
            // while the resident is up on the week controls.
            const selected = dayCol === i;
            const filled = focused || selected;
            return (
              <div
                key={date}
                style={{
                  borderRadius: 22,
                  padding: "18px 12px 16px",
                  textAlign: "center",
                  background: filled ? p.gCore : isToday ? p.gTint : p.surface,
                  border: `1px solid ${filled ? p.gCore : isToday ? p.gSoft : p.hairSoft}`,
                  color: filled ? "#fff" : p.ink,
                  boxShadow: focused
                    ? `0 0 0 4px ${p.paper}, 0 0 0 10px ${p.gCore}`
                    : "none",
                  transition: "transform 120ms ease",
                  transform: focused ? "translateY(-2px)" : "none",
                }}
              >
                <div
                  style={{
                    fontFamily: p.mono,
                    fontSize: 16,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: filled ? "rgba(255,255,255,0.82)" : p.inkMuted,
                  }}
                >
                  {ISO_WEEKDAYS_SV_SHORT[i]}
                </div>
                <div
                  style={{
                    fontSize: 42,
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: "-0.02em",
                    marginTop: 2,
                  }}
                >
                  {d ? d.getDate() : "-"}
                </div>
                {/* A dot means "there is food written down for this day" - the
                    whole week's state readable without moving the focus. */}
                <div
                  style={{
                    marginTop: 8,
                    height: 10,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: dayMeals.length
                        ? filled
                          ? "rgba(255,255,255,0.9)"
                          : p.gCore
                        : "transparent",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* The selected day's servings. */}
        {loading && !weeks.length ? (
          emptyCard("Hämtar matsedeln…")
        ) : failed && !weeks.length ? (
          emptyCard("Matsedeln är inte tillgänglig just nu.")
        ) : meals.length === 0 ? (
          emptyCard(
            `Ingen meny inlagd för ${(ISO_WEEKDAYS_SV[Math.min(dayCol, 6)] ?? "").toLowerCase()}.`
          )
        ) : (
          <div
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns:
                meals.length === 1
                  ? "1fr"
                  : "repeat(auto-fit, minmax(380px, 1fr))",
              gap: 24,
              minHeight: 0,
              alignContent: "start",
              overflow: "hidden",
            }}
          >
            {meals.map((m) => (
              <div
                key={m.id}
                style={{
                  ...careCard,
                  background: p.surface,
                  border: `1px solid ${p.hairSoft}`,
                  padding: "34px 38px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  minHeight: 0,
                }}
              >
                <div
                  style={{
                    fontFamily: p.mono,
                    fontSize: 17,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: p.gCore,
                  }}
                >
                  {m.label}
                </div>
                <div
                  style={{
                    fontSize: 40,
                    fontWeight: 600,
                    // 1.25 rather than a tighter display value: these lines are
                    // full of å/ä/ö/g/j and the card clips its overflow.
                    lineHeight: 1.25,
                    letterSpacing: "-0.015em",
                    color: p.ink,
                    whiteSpace: "pre-line",
                  }}
                >
                  {m.body}
                </div>
                {m.note && (
                  <div
                    style={{
                      fontSize: 26,
                      lineHeight: 1.35,
                      color: p.inkSoft,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {m.note}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
      <Hint
        keys={[
          { cap: "◀ ▶", label: "Byt dag" },
          { cap: "▲", label: "Vecka & tillbaka" },
        ]}
        right={result?.stale ? "Senast hämtade menyn" : undefined}
      />
    </div>
  );
}

// ── Väder (weather) ──────────────────────────────────────────────────────────

const CARE_WX: Record<string, { glyph: string; label: string }> = {
  clearsky: { glyph: "☀", label: "Klart" },
  fair: { glyph: "🌤", label: "Lätt molnigt" },
  partlycloudy: { glyph: "⛅", label: "Halvklart" },
  cloudy: { glyph: "☁", label: "Molnigt" },
  fog: { glyph: "🌫", label: "Dimma" },
  lightrain: { glyph: "🌦", label: "Lätt regn" },
  rain: { glyph: "🌧", label: "Regn" },
  heavyrain: { glyph: "🌧", label: "Kraftigt regn" },
  rainshowers: { glyph: "🌦", label: "Regnskurar" },
  lightsnow: { glyph: "🌨", label: "Lätt snö" },
  snow: { glyph: "❄", label: "Snö" },
  heavysnow: { glyph: "❄", label: "Snö" },
  snowshowers: { glyph: "🌨", label: "Snöbyar" },
  sleet: { glyph: "🌨", label: "Snöblandat" },
  thunder: { glyph: "⛈", label: "Åska" },
};
function careWx(symbol: string): { glyph: string; label: string } {
  if (CARE_WX[symbol]) return CARE_WX[symbol];
  const hit = Object.keys(CARE_WX)
    .filter((k) => symbol.includes(k))
    .sort((a, b) => b.length - a.length)[0];
  return hit ? CARE_WX[hit] : { glyph: "·", label: symbol || "-" };
}
interface CareWeatherWire {
  current: { tempC: number; symbol: string };
  days: { date: string; minC: number; maxC: number; symbol: string }[];
}

function CareWeather({ go, scopeRef, context }: ScreenProps) {
  const p = useC();
  const units = useScheme().content.weather?.units ?? "c";
  const backFocused = useBackOnly(go, scopeRef, useBackTarget("weather"));
  const [data, setData] = useState<CareWeatherWire | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (context.lat == null || context.lng == null) return;
    let alive = true;
    setErr(false);
    fetch(`/api/frontlayer/v1/weather?lat=${context.lat}&lon=${context.lng}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j: CareWeatherWire) => alive && setData(j))
      .catch(() => alive && setErr(true));
    return () => {
      alive = false;
    };
  }, [context.lat, context.lng]);
  const fmt = (c: number) =>
    units === "f" ? `${Math.round(c * (9 / 5) + 32)}°` : `${c}°`;
  const now = data ? careWx(data.current.symbol) : null;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main style={subMain}>
        <SubTitle
          eyebrow={context.city || "Prognos"}
          title="Väder"
          backFocused={backFocused}
        />
        {err ? (
          <div
            style={{
              ...careCard,
              background: p.surface,
              border: `1px solid ${p.hairSoft}`,
              flex: 1,
              padding: "56px 64px",
              fontSize: 30,
              color: p.inkSoft,
            }}
          >
            Vädret är inte tillgängligt just nu.
          </div>
        ) : !data ? (
          <div
            style={{
              ...careCard,
              background: p.surface,
              border: `1px solid ${p.hairSoft}`,
              flex: 1,
              padding: "56px 64px",
              fontSize: 30,
              color: p.inkSoft,
            }}
          >
            Laddar…
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", gap: 32, minHeight: 0 }}>
            <div
              style={{
                background: p.gDeep,
                color: "#fff",
                borderRadius: 32,
                flex: "0 0 38%",
                padding: "48px 56px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 12,
              }}
            >
              <div
                style={{
                  fontFamily: p.mono,
                  fontSize: 15,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: p.gGlow,
                }}
              >
                Just nu
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
                <span style={{ fontSize: 120, lineHeight: 1 }}>
                  {now?.glyph}
                </span>
                <span
                  style={{
                    fontSize: 110,
                    fontWeight: 700,
                    letterSpacing: "-0.04em",
                  }}
                >
                  {fmt(data.current.tempC)}
                </span>
              </div>
              <div style={{ fontSize: 32, color: p.gGlow }}>{now?.label}</div>
            </div>
            <div
              style={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: `repeat(${Math.min(4, data.days.length)}, 1fr)`,
                gap: 24,
              }}
            >
              {data.days.slice(0, 4).map((d) => {
                const w = careWx(d.symbol);
                const day = new Date(`${d.date}T12:00:00Z`).toLocaleDateString(
                  "sv-SE",
                  { weekday: "short" }
                );
                return (
                  <div
                    key={d.date}
                    style={{
                      background: p.surface,
                      border: `1px solid ${p.hairSoft}`,
                      borderRadius: 28,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: 20,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: p.mono,
                        fontSize: 18,
                        textTransform: "uppercase",
                        color: p.inkMuted,
                      }}
                    >
                      {day}
                    </div>
                    <div style={{ fontSize: 64 }}>{w.glyph}</div>
                    <div
                      style={{ fontSize: 34, fontWeight: 700, color: p.ink }}
                    >
                      {fmt(d.maxC)}
                    </div>
                    <div style={{ fontSize: 24, color: p.inkSoft }}>
                      {fmt(d.minC)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
      <Hint keys={[{ cap: "◀", label: "Tillbaka" }]} />
    </div>
  );
}

// ── In-room care whiteboard (Care-P2) ─────────────────────────────────────

interface WhiteboardProps extends ScreenProps {
  realtimeEvent?: RealtimeEvent | null;
}

/** One titled panel on the whiteboard. `tone="alert"` = safety-critical (red). */
function WbPanel({
  label,
  tone = "normal",
  children,
}: {
  label: string;
  tone?: "normal" | "alert";
  children: ReactNode;
}) {
  const p = useC();
  const alert = tone === "alert";
  return (
    <section
      style={{
        background: alert ? "#FCECEC" : p.surface,
        border: `1px solid ${alert ? "#E4B4B4" : p.hairSoft}`,
        borderRadius: 28,
        padding: "32px 36px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        minHeight: 0,
      }}
    >
      <div
        style={{
          fontFamily: p.mono,
          fontSize: 16,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: alert ? "#B23B3B" : p.gCore,
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div style={{ color: p.ink }}>{children}</div>
    </section>
  );
}

/**
 * In-room care whiteboard - the clinical safety summary the resident and their
 * family see on the room TV: allergies, isolation, fall risk, diet, precautions,
 * and the care team. Fetched from
 * /api/frontlayer/v1/premise/:id/rooms/:rid/care/whiteboard (no PHI identifiers - only
 * the already-reduced display name), auto-refreshing every 30 s and immediately
 * on a `care_profile_changed` realtime event.
 *
 * Controls: any D-pad key / OK / Back returns home.
 */
function Whiteboard({ go, scopeRef, context, realtimeEvent }: WhiteboardProps) {
  const p = useC();
  const { projection, loading } = useRoomWhiteboard(
    true,
    realtimeEvent,
    context.premiseId,
    context.roomId
  );
  const backFocused = useBackOnly(go, scopeRef, "home");
  const profile = projection?.profile ?? null;
  const name = projection?.displayName?.trim();

  const bigList = (items: string[], color?: string) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
      {items.map((it, i) => (
        <span
          key={i}
          style={{
            fontSize: 30,
            fontWeight: 600,
            padding: "8px 18px",
            borderRadius: 999,
            background: color ? "#fff" : p.paper,
            border: `1px solid ${color ?? p.hairSoft}`,
            color: color ?? p.ink,
          }}
        >
          {it}
        </span>
      ))}
    </div>
  );

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main style={subMain}>
        <SubTitle
          eyebrow="Vårdöversikt"
          title={name ? `Min vård · ${name}` : "Min vård"}
          backFocused={backFocused}
        />
        {loading && !profile ? (
          <div style={{ fontSize: 30, color: p.inkSoft }}>Laddar…</div>
        ) : !profile ? (
          <div
            style={{
              flex: "1 1 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 34,
              color: p.inkSoft,
            }}
          >
            Ingen vårdinformation ännu.
          </div>
        ) : (
          <div
            className="foyer-scheme-scroll"
            style={{
              flex: "1 1 auto",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridAutoRows: "min-content",
              gap: 28,
              overflow: "auto",
              alignContent: "start",
              minHeight: 0,
            }}
          >
            <div style={{ gridColumn: "1 / -1" }}>
              <WbPanel label="Allergier" tone="alert">
                {profile.allergies.length > 0 ? (
                  bigList(profile.allergies, "#B23B3B")
                ) : (
                  <span style={{ fontSize: 28, color: p.inkSoft }}>
                    Inga kända allergier
                  </span>
                )}
              </WbPanel>
            </div>

            <WbPanel
              label="Isolering"
              tone={profile.isolation ? "alert" : "normal"}
            >
              <span style={{ fontSize: 34, fontWeight: 700 }}>
                {profile.isolation ?? "Ingen"}
              </span>
            </WbPanel>

            <WbPanel
              label="Fallrisk"
              tone={profile.fallRisk ? "alert" : "normal"}
            >
              <span style={{ fontSize: 34, fontWeight: 700 }}>
                {profile.fallRisk ? "Ja - var försiktig" : "Nej"}
              </span>
            </WbPanel>

            {profile.diet && (
              <WbPanel label="Kost">
                <span style={{ fontSize: 32, fontWeight: 600 }}>
                  {profile.diet}
                </span>
              </WbPanel>
            )}

            {profile.precautions.length > 0 && (
              <WbPanel label="Att tänka på">
                {bigList(profile.precautions)}
              </WbPanel>
            )}

            {profile.careTeam.length > 0 && (
              <div style={{ gridColumn: "1 / -1" }}>
                <WbPanel label="Vårdteam">
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "14px 40px",
                    }}
                  >
                    {profile.careTeam.map((m, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 16,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: p.mono,
                            fontSize: 18,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: p.inkMuted,
                            minWidth: 200,
                          }}
                        >
                          {m.role}
                        </span>
                        <span style={{ fontSize: 30, fontWeight: 600 }}>
                          {m.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </WbPanel>
              </div>
            )}

            {profile.notes && (
              <div style={{ gridColumn: "1 / -1" }}>
                <WbPanel label="Meddelande">
                  <p
                    style={{
                      fontSize: 30,
                      lineHeight: 1.35,
                      margin: 0,
                      color: p.ink,
                    }}
                  >
                    {profile.notes}
                  </p>
                </WbPanel>
              </div>
            )}
          </div>
        )}
      </main>
      <Hint keys={[{ cap: "◀", label: "Tillbaka" }]} right="omsorgsskärm" />
    </div>
  );
}

// ── Call staff / service requests (Care-P2) ───────────────────────────────

interface CallStaffProps extends ScreenProps {
  realtimeEvent?: RealtimeEvent | null;
}

const CALL_OPTIONS: {
  category: CareTaskCategory;
  label: string;
  hint: string;
  glyph: (p: { size?: number }) => ReactNode;
}[] = [
  {
    category: "assistance",
    label: "Hjälp",
    hint: "Allmän hjälp",
    glyph: Icon.Bell,
  },
  {
    category: "water",
    label: "Vatten",
    hint: "Något att dricka",
    glyph: Icon.Coffee,
  },
  {
    category: "restroom",
    label: "Toalett",
    hint: "Hjälp till toaletten",
    glyph: Icon.House,
  },
  { category: "pain", label: "Smärta", hint: "Jag har ont", glyph: Icon.Heart },
  {
    category: "comfort",
    label: "Bekvämlighet",
    hint: "Kudde, filt, ljus",
    glyph: Icon.Leaf,
  },
  { category: "other", label: "Annat", hint: "Något annat", glyph: Icon.Info },
];

/**
 * In-room "call staff" screen. The resident picks a request category; it POSTs
 * to /care/requests and appears on the CMS staff queue. Pending calls for the
 * room are shown so the resident sees "staff is on the way" (refreshed on a
 * `care_task_changed` realtime event and a 15 s poll). Any pick that already has
 * an open request is de-duplicated server-side.
 */
function CallStaff({ go, scopeRef, context, realtimeEvent }: CallStaffProps) {
  const p = useC();
  const { pending, raising, error, raise } = useServiceRequests(
    true,
    realtimeEvent,
    context.premiseId,
    context.roomId
  );
  const cols = 2;
  const contentRows = Math.ceil(CALL_OPTIONS.length / cols);
  // Row 0 is the Tillbaka pill; the option grid starts at row 1.
  const [focus] = useFocusGrid({
    rows: contentRows + 1,
    cols,
    scopeRef,
    initial: { r: 1, c: 0 },
    wrap: true,
    onEnter: (f) => {
      if (f.r === 0) {
        go("home");
        return;
      }
      const opt = CALL_OPTIONS[(f.r - 1) * cols + f.c];
      if (opt) void raise(opt.category);
    },
    onBack: () => go("home"),
  });
  const backFocused = focus.r === 0;
  const focusIdx = backFocused ? -1 : (focus.r - 1) * cols + focus.c;
  const pendingCats = new Set(pending.map((t) => t.category));

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main style={subMain}>
        <SubTitle
          eyebrow="Hjälp"
          title="Ring personal"
          backFocused={backFocused}
        />

        {pending.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 20,
              background: p.gTint,
              border: `1px solid ${p.gCore}`,
              borderRadius: 24,
              padding: "24px 32px",
              color: p.gDeep,
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: 999,
                background: p.gCore,
                boxShadow: `0 0 0 6px ${p.gGlow}`,
                animation: "pulseGlow 2s ease-in-out infinite",
                flex: "0 0 auto",
              }}
            />
            <span style={{ fontSize: 32, fontWeight: 700 }}>
              Personal är på väg.
            </span>
          </div>
        )}

        {error && (
          <div style={{ fontSize: 24, color: "#B23B3B" }}>
            Kunde inte skicka - försök igen.
          </div>
        )}

        <div
          style={{
            flex: "1 1 auto",
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gridAutoRows: "1fr",
            gap: 28,
            minHeight: 0,
          }}
        >
          {CALL_OPTIONS.map((opt, i) => {
            const focused = i === focusIdx;
            const active = pendingCats.has(opt.category);
            const Glyph = opt.glyph;
            return (
              <article
                key={opt.category}
                onClick={() => void raise(opt.category)}
                style={{
                  position: "relative",
                  background: active ? p.gTint : p.surface,
                  border: `1px solid ${
                    focused ? "transparent" : active ? p.gCore : p.hairSoft
                  }`,
                  borderRadius: 28,
                  display: "grid",
                  gridTemplateColumns: "160px 1fr",
                  alignItems: "center",
                  gap: 24,
                  padding: "28px 36px",
                  cursor: "pointer",
                  boxShadow: focused ? FOCUS_SHADOW(p) : "none",
                  transform: focused ? "translateY(-2px)" : "none",
                  transition: "transform 160ms ease",
                  opacity: raising ? 0.7 : 1,
                }}
              >
                <span
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: 24,
                    display: "grid",
                    placeItems: "center",
                    background: p.gTint,
                    border: `1px solid ${p.gSoft}`,
                    color: p.gDeep,
                  }}
                >
                  <Glyph size={64} />
                </span>
                <div>
                  <div
                    style={{
                      fontSize: 56,
                      fontWeight: 700,
                      letterSpacing: "-0.03em",
                      color: p.ink,
                    }}
                  >
                    {opt.label}
                  </div>
                  <div style={{ fontSize: 24, color: p.inkSoft, marginTop: 4 }}>
                    {active ? "Skickat - personal är på väg" : opt.hint}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </main>
      <Hint
        keys={[
          { cap: "◀▶▲▼", label: "Välj" },
          { cap: "OK", label: "Ring" },
          { cap: "◀", label: "Tillbaka" },
        ]}
      />
    </div>
  );
}

// ── Videosamtal - the resident's directory of faces (VC-15) ───────────────

interface VideoCallProps extends ScreenProps {
  realtimeEvent?: RealtimeEvent | null;
}

/** Six faces to a screen. More than that and nothing is big enough to aim at
 *  from a chair three metres away, which is the whole design constraint. */
const FACES_PER_PAGE = 6;
const FACE_COLS = 3;

/**
 * Why the grid is empty, said out loud in Swedish.
 *
 * Every one of these is a sentence a resident or a passing carer can act on -
 * a blank grid teaches nobody anything, and "no contacts" and "you turned this
 * off" call for completely different help. The premise-level reasons normally
 * never render (the tile is hidden - see `callsHiddenForPremise`), but the CMS
 * preview and a policy that flips while the screen is open both reach them.
 */
function emptyCopy(reason: TvCallDirectoryReason | null): {
  title: string;
  body: string;
} {
  switch (reason) {
    case "no_contacts":
      return {
        title: "Inga kontakter än",
        body: "Personalen lägger till dina anhöriga här. Säg till dem vem du vill kunna ringa.",
      };
    case "resident_opted_out":
      return {
        title: "Videosamtal är avstängt för dig",
        body: "Vill du kunna ringa igen? Säg till personalen, så slår de på det.",
      };
    case "calls_disabled":
    case "outbound_disabled":
    case "not_a_care_premise":
      return {
        title: "Videosamtal är avstängt här",
        body: "Boendet har inte slagit på videosamtal. Personalen kan ändra det.",
      };
    case "no_active_resident":
      return {
        title: "Ingen boende i rummet",
        body: "Videosamtal öppnas när rummet får en boende.",
      };
    default:
      return {
        title: "Inga kontakter att visa",
        body: "Vi kunde inte hämta dina kontakter just nu. Försök igen om en stund.",
      };
  }
}

/**
 * The camera side of the same question. `videoCallMode()` returning null means
 * nothing could answer (plain web, CMS preview, an older shell) - that is NOT
 * "no camera", so we say nothing and let the grid render.
 */
function cameraCopy(): { title: string; body: string } | null {
  const mode = bridge.videoCallMode();
  switch (mode) {
    case "no-camera":
    case "none":
      return {
        title: "Ingen kamera hittades",
        body: "Kameran verkar inte vara inkopplad. Säg till personalen, så fixar de den.",
      };
    case "no-permission":
      return {
        title: "Kameran är inte påslagen",
        body: "TV:n får inte använda kameran än. Personalen behöver godkänna det.",
      };
    default:
      return null;
  }
}

/** Big centred message that replaces the grid. One idea, two sizes of type. */
function VideoCallEmpty({ title, body }: { title: string; body: string }) {
  const p = useC();
  return (
    <div
      style={{
        flex: "1 1 auto",
        display: "grid",
        placeItems: "center",
        minHeight: 0,
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          textAlign: "center",
          background: p.surface,
          border: `1px solid ${p.hairSoft}`,
          borderRadius: 40,
          padding: "72px 80px",
        }}
      >
        <span
          style={{
            display: "inline-grid",
            placeItems: "center",
            width: 160,
            height: 160,
            borderRadius: 40,
            background: p.gTint,
            border: `1px solid ${p.gSoft}`,
            color: p.gDeep,
            marginBottom: 36,
          }}
        >
          <Icon.Video size={92} />
        </span>
        <div
          style={{
            fontSize: 64,
            fontWeight: 700,
            letterSpacing: "-0.03em",
            color: p.ink,
            lineHeight: 1.05,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 32,
            color: p.inkSoft,
            marginTop: 20,
            lineHeight: 1.35,
          }}
        >
          {body}
        </div>
      </div>
    </div>
  );
}

/** Initials fallback when a contact has no photo. One letter - two initials
 *  read as an abbreviation, and the name is written underneath anyway. */
function initialOf(name: string): string {
  return (name.trim()[0] ?? "?").toUpperCase();
}

function FaceCard({
  entry,
  focused,
  onSelect,
}: {
  entry: TvCallDirectoryEntry;
  focused: boolean;
  onSelect: () => void;
}) {
  const p = useC();
  return (
    <article
      onClick={onSelect}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 20,
        padding: "32px 24px",
        background: p.surface,
        border: `1px solid ${focused ? "transparent" : p.hairSoft}`,
        borderRadius: 36,
        cursor: "pointer",
        boxShadow: focused ? FOCUS_SHADOW(p) : "none",
        transform: focused ? "translateY(-4px)" : "none",
        transition: "transform 160ms ease",
        minHeight: 0,
      }}
    >
      <div
        style={{
          width: 240,
          height: 240,
          borderRadius: 999,
          overflow: "hidden",
          flex: "0 0 auto",
          background: p.gTint,
          border: `4px solid ${focused ? p.gCore : p.gSoft}`,
          display: "grid",
          placeItems: "center",
          color: p.gDeep,
        }}
      >
        {entry.avatarUrl ? (
          <img
            src={entry.avatarUrl}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span style={{ fontSize: 110, fontWeight: 700, lineHeight: 1 }}>
            {initialOf(entry.displayName)}
          </span>
        )}
      </div>
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            fontSize: 54,
            fontWeight: 700,
            letterSpacing: "-0.03em",
            color: p.ink,
            lineHeight: 1.05,
          }}
        >
          {entry.displayName}
        </div>
        {entry.relation && (
          <div style={{ fontSize: 30, color: p.inkSoft, marginTop: 6 }}>
            {entry.relation}
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * The faces a resident may call, six to a screen.
 *
 * Selecting one is deliberately a STUB until VC-20 lands: it opens a panel that
 * names the person and says the call is coming. That is enough to answer the
 * question this feature actually turns on - whether an 85-year-old with a
 * remote can find the right face and press OK unaided - without any media
 * stack existing yet.
 */
function VideoCallDirectory({
  go,
  scopeRef,
  context,
  realtimeEvent,
}: VideoCallProps) {
  const p = useC();
  const { entries, reason, ready, error } = useCallDirectory(
    true,
    realtimeEvent,
    context.premiseId,
    context.roomId
  );
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<TvCallDirectoryEntry | null>(null);

  // The tile that leads here is gated on the same answer, but the answer can
  // change under a resident who is already standing on this screen (a seat
  // released, the premise switching calling off, a discharge). The screen must
  // not become a dead end, so it walks itself back home - unless a call is
  // already up, which the call surface owns.
  useEffect(() => {
    if (ready && !selected && callsHiddenForPremise(reason)) go("home");
  }, [ready, reason, selected, go]);

  const camera = cameraCopy();
  const pageCount = Math.max(1, Math.ceil(entries.length / FACES_PER_PAGE));
  // A page that emptied under us (a relative was revoked) must not strand the
  // resident on a blank sheet.
  const safePage = Math.min(page, pageCount - 1);
  const pageEntries = entries.slice(
    safePage * FACES_PER_PAGE,
    safePage * FACES_PER_PAGE + FACES_PER_PAGE
  );
  const hasPager = entries.length > FACES_PER_PAGE;
  const showGrid = !camera && pageEntries.length > 0;

  // Row 0 is the Tillbaka pill; the faces start at row 1; the pager (when the
  // resident has more than six contacts) is the last row.
  const gridRows = showGrid ? Math.ceil(pageEntries.length / FACE_COLS) : 0;
  const rows = 1 + gridRows + (showGrid && hasPager ? 1 : 0);
  const pagerRow = showGrid && hasPager ? rows - 1 : -1;

  const [focus, setFocus] = useFocusGrid({
    rows,
    cols: showGrid ? FACE_COLS : 1,
    scopeRef,
    initial: { r: showGrid ? 1 : 0, c: 0 },
    wrap: true,
    enabled: !selected,
    onEnter: (f) => {
      if (f.r === 0) {
        go("home");
        return;
      }
      if (f.r === pagerRow) {
        setPage((prev) => (prev + 1) % pageCount);
        setFocus({ r: 1, c: 0 });
        return;
      }
      const entry = pageEntries[(f.r - 1) * FACE_COLS + f.c];
      if (entry) setSelected(entry);
    },
    onBack: () => go("home"),
  });

  // The grid does not exist on the first render (the directory is still in
  // flight), so `initial` cannot land on a face. Move focus onto the first face
  // the moment the faces appear - the resident's first press of OK must call
  // someone, not walk back out of the screen. Only while focus is still on the
  // Back pill, so it never yanks focus from under someone already navigating.
  const facesArrived = useRef(false);
  useEffect(() => {
    if (!showGrid) {
      facesArrived.current = false;
      return;
    }
    if (facesArrived.current) return;
    facesArrived.current = true;
    setFocus({ r: 1, c: 0 });
  }, [showGrid, setFocus]);

  const backFocused = focus.r === 0;
  const pagerFocused = focus.r === pagerRow;
  const focusIdx =
    backFocused || pagerFocused ? -1 : (focus.r - 1) * FACE_COLS + focus.c;
  const empty = emptyCopy(error && entries.length === 0 ? null : reason);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main style={subMain}>
        <SubTitle
          eyebrow="Ring familjen"
          title="Videosamtal"
          right={hasPager ? `Sida ${safePage + 1} av ${pageCount}` : undefined}
          backFocused={backFocused}
        />

        {camera ? (
          <VideoCallEmpty title={camera.title} body={camera.body} />
        ) : !ready ? (
          <div
            style={{
              flex: "1 1 auto",
              display: "grid",
              placeItems: "center",
              fontSize: 36,
              color: p.inkSoft,
            }}
          >
            Hämtar dina kontakter…
          </div>
        ) : pageEntries.length === 0 ? (
          <VideoCallEmpty title={empty.title} body={empty.body} />
        ) : (
          <>
            <div
              style={{
                flex: "1 1 auto",
                display: "grid",
                gridTemplateColumns: `repeat(${FACE_COLS}, 1fr)`,
                gridAutoRows: "1fr",
                gap: 32,
                minHeight: 0,
              }}
            >
              {pageEntries.map((entry, i) => (
                <FaceCard
                  key={entry.id}
                  entry={entry}
                  focused={i === focusIdx}
                  onSelect={() => setSelected(entry)}
                />
              ))}
            </div>
            {hasPager && (
              <div
                onClick={() => setPage((prev) => (prev + 1) % pageCount)}
                style={{
                  alignSelf: "center",
                  padding: "20px 48px",
                  borderRadius: 999,
                  fontSize: 32,
                  fontWeight: 700,
                  cursor: "pointer",
                  background: pagerFocused ? p.gCore : p.surface,
                  color: pagerFocused ? "#fff" : p.ink,
                  border: `1px solid ${pagerFocused ? p.gCore : p.hairSoft}`,
                  boxShadow: pagerFocused ? FOCUS_SHADOW(p) : "none",
                }}
              >
                Visa fler personer ▾
              </div>
            )}
          </>
        )}
      </main>
      <Hint
        keys={
          showGrid
            ? [
                { cap: "◀▶▲▼", label: "Välj person" },
                { cap: "OK", label: "Ring" },
                { cap: "◀", label: "Tillbaka" },
              ]
            : [{ cap: "◀", label: "Tillbaka" }]
        }
      />
      {selected && (
        <OutboundCall
          entry={selected}
          scopeRef={scopeRef}
          realtimeEvent={realtimeEvent}
          onExit={() => {
            setSelected(null);
            go("home");
          }}
        />
      )}
    </div>
  );
}

/* ── Videosamtal - the outbound call, from ring to hang-up (VC-20) ────────── */

/** The phases the resident actually sees. `active` is the only one that opens a
 *  camera - it is also the only one that mounts the LiveKit surface. */
type OutboundPhase = "placing" | "ringing" | "active" | "ended";

/** Why an outbound call finished, in the vocabulary the resident is shown. It
 *  unifies the server's `CallEndReason`, the media surface's {@link
 *  CallEndCause}, and the local "the invite itself was refused" cases. */
type OutboundEndKind =
  | "declined"
  | "timeout"
  | "busy"
  | "network"
  | "remote_hangup"
  | "media_error"
  | "failed";

/** The invite response - token + the SFU url it is inert without (VC-20). */
interface InviteOk {
  session: { id: string; expiresAt: string | null };
  token: string;
  url: string;
}

/**
 * Map an end kind to a calm Swedish sentence + a heading. Every one routes
 * home; the copy is what tells the resident which "no" they hit.
 *
 * Written as a SET, not one at a time (VC3-9). Four rules hold across all of
 * them, because they are the same voice speaking to the same person:
 *
 *   1. The title says what happened; the body says what to do next. A card that
 *      only names a failure leaves an 80-year-old with nowhere to go.
 *   2. No system vocabulary. The resident is not "a room", the network is not
 *      "anslutningen", and nothing "gick fel" without saying what.
 *   3. Never imply the resident caused it, and never ask them to diagnose it.
 *      "Säg till personalen, så hjälper de dig" is the register the whole set
 *      is tuned to - it was already right in `media_error`.
 *   4. Say who, when we know who. `${name}` beats "personen du ringde".
 */
function endedCopy(
  kind: OutboundEndKind,
  name: string
): { title: string; body: string } {
  switch (kind) {
    case "declined":
      return {
        title: "Kunde inte svara",
        body: `${name} kunde inte svara just nu. Försök gärna igen senare.`,
      };
    case "timeout":
      return {
        title: "Inget svar",
        body: `${name} svarade inte den här gången. Försök gärna igen senare.`,
      };
    case "busy":
      // "Rummet är redan i ett samtal" was the server's word for it. A resident
      // does not think of themselves as a room.
      return {
        title: "Upptaget",
        body: "Det pågår redan ett samtal här. Försök igen om en liten stund.",
      };
    case "network":
      return {
        title: "Samtalet bröts",
        body: "Uppkopplingen försvann. Försök gärna igen om en stund.",
      };
    case "remote_hangup":
      return {
        title: "Samtalet avslutades",
        body: `${name} avslutade samtalet.`,
      };
    case "media_error":
      return {
        title: "Kameran startade inte",
        body: "Vi kunde inte starta kameran. Säg till personalen, så hjälper de dig.",
      };
    case "failed":
    default:
      return {
        title: "Det gick inte att ringa",
        body: "Vi kunde inte koppla samtalet just nu. Försök igen om en stund.",
      };
  }
}

/** A server `CallEndReason` (from a `call_state` event) → our end kind. */
function endKindFromReason(reason: CallEndReason | null): OutboundEndKind {
  switch (reason) {
    case "answered_then_hangup":
      return "remote_hangup";
    case "declined":
      return "declined";
    case "timeout":
      return "timeout";
    case "busy":
      return "busy";
    case "error":
      return "media_error";
    case "policy_block":
    default:
      return "failed";
  }
}

/** A media-surface {@link CallEndCause} → our end kind. */
function endKindFromCause(cause: CallEndCause): OutboundEndKind {
  return cause === "remote_left"
    ? "remote_hangup"
    : cause === "network"
      ? "network"
      : "media_error";
}

function callHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const b = getBinding();
  if (b?.screenToken) headers["X-Screen-Token"] = b.screenToken;
  return headers;
}

/**
 * The whole outbound call, launched when a resident presses a face (§3.2):
 * POST the invite, show "Ringer …", join the media room on answer, and end on
 * Back from anywhere with no confirmation. Every terminal state - declined,
 * timeout, busy, network loss, the callee hanging up, or a failure to place the
 * call at all - shows a plain-Swedish line and routes home.
 *
 * Two safety rules run through it. The camera opens only in `active`: the
 * LiveKit surface (the sole `getUserMedia` caller) is mounted only then, and the
 * native gate it arms is disarmed on EVERY exit including error and unmount
 * (VC-13, §4.2). And Back is wired to end the call, never to a dialog.
 */
function OutboundCall({
  entry,
  scopeRef,
  realtimeEvent,
  onExit,
}: {
  entry: TvCallDirectoryEntry;
  scopeRef: RefObject<HTMLElement>;
  realtimeEvent?: RealtimeEvent | null;
  onExit: () => void;
}) {
  const p = useC();
  // The call surfaces share one palette with the incoming ring (VC3-9) so a
  // resident meets the same card, the same pills and the same type scale in
  // both directions.
  const callT = callTheme(useScheme().tokens);
  const [phase, setPhase] = useState<OutboundPhase>("placing");
  const [endKind, setEndKind] = useState<OutboundEndKind>("failed");
  const call = useRef<InviteOk | null>(null);
  const name = entry.displayName;

  // A single funnel into the terminal state, so no two signals (a realtime
  // event racing a LiveKit disconnect) can double-transition or reopen a closed
  // call. First writer wins.
  const settled = useRef(false);
  const settle = useCallback((kind: OutboundEndKind) => {
    if (settled.current) return;
    settled.current = true;
    setEndKind(kind);
    setPhase("ended");
  }, []);

  const goActive = useCallback(() => {
    if (settled.current) return;
    setPhase((cur) =>
      cur === "ringing" || cur === "placing" ? "active" : cur
    );
  }, []);

  /** End on the server (best effort) - the media teardown rides unmount. */
  const hangUp = useCallback(() => {
    const c = call.current;
    if (!c) return;
    void fetch(
      `/api/videocall/v1/call/${encodeURIComponent(c.session.id)}/end`,
      {
        method: "POST",
        headers: callHeaders(),
      }
    ).catch(() => {});
  }, []);

  // Place the call once, on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/videocall/v1/call/invite", {
          method: "POST",
          headers: callHeaders(),
          body: JSON.stringify({ contactId: entry.id }),
        });
        if (cancelled) return;
        if (res.status === 201) {
          call.current = (await res.json()) as InviteOk;
          setPhase("ringing");
        } else {
          const body = (await res.json().catch(() => ({}))) as {
            error?: string;
          };
          settle(body.error === "room_busy" ? "busy" : "failed");
        }
      } catch {
        if (!cancelled) settle("failed");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Belt-and-braces: disarm the native capture gate on unmount whatever path we
  // leave by. The media surface also does this, but a call torn down before it
  // ever mounted (Back while ringing) must not depend on it. Idempotent.
  useEffect(() => {
    return () => bridge.endCallSession();
  }, []);

  // The ring's own deadline. If the socket drops we may never be told the ring
  // expired, so stop ourselves - 40 seconds is a promise the resident can hear.
  useEffect(() => {
    if (phase !== "ringing") return;
    const at = call.current?.session.expiresAt;
    if (!at) return;
    const due = Date.parse(at);
    if (!Number.isFinite(due)) return;
    const id = setTimeout(
      () => settle("timeout"),
      Math.max(0, due + 2000 - Date.now())
    );
    return () => clearTimeout(id);
  }, [phase, settle]);

  // Resilience for the ONE transition that matters most - "join on answer". A
  // dropped realtime event must not leave the resident ringing at a call the
  // callee already picked up, so while ringing we also poll the room snapshot.
  useEffect(() => {
    if (phase !== "ringing") return;
    let stop = false;
    const tick = async () => {
      const snap = await reconcileCall({
        premiseId: getBinding()?.premiseId ?? "",
        roomId: getBinding()?.roomId ?? "",
        screenToken: getBinding()?.screenToken,
      });
      if (stop || !call.current) return;
      if (snap && snap.callId === call.current.session.id) {
        if (snap.state === "active") goActive();
      } else if (snap === null) {
        // The call is gone from the room's live state - it ended while we were
        // ringing. We have no reason code here, so assume the common one.
        settle("timeout");
      }
    };
    const id = setInterval(() => void tick(), 2500);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [phase, goActive, settle]);

  // Fold the room's `call_state` for OUR call - the authoritative signal, with
  // the reason code the poll above lacks.
  useEffect(() => {
    const c = call.current;
    if (!c || !realtimeEvent || realtimeEvent.type !== "call_state") return;
    if (realtimeEvent.callId !== c.session.id) return;
    if (realtimeEvent.state === "active") goActive();
    else if (realtimeEvent.state === "ended") {
      settle(endKindFromReason(realtimeEvent.endReason));
    }
  }, [realtimeEvent, goActive, settle]);

  // Keys: Back ends the call from any phase (no confirm); OK dismisses only the
  // ended card. The directory grid is disabled while we are mounted, so this is
  // the only handler live on the scope.
  useFocusGrid({
    rows: 1,
    cols: 1,
    scopeRef,
    enabled: true,
    onEnter: () => {
      if (phase === "ended") onExit();
    },
    onBack: () => {
      if (phase !== "ended") hangUp();
      onExit();
    },
  });

  // Auto-return home a few seconds after a terminal message, so a resident who
  // walks away doesn't leave the card up forever.
  useEffect(() => {
    if (phase !== "ended") return;
    const id = setTimeout(onExit, 6000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (phase === "active" && call.current) {
    return (
      <div style={{ position: "absolute", inset: 0, zIndex: 20 }}>
        <Suspense
          fallback={
            <CallVeil
              theme={callT}
              label={`Ansluter till ${name}…`}
              note="Det tar bara någon sekund."
            />
          }
        >
          <CallMediaSurface
            token={call.current.token}
            url={call.current.url}
            peerName={name}
            theme={callT}
            onEnded={(cause) => settle(endKindFromCause(cause))}
          />
        </Suspense>
        {/* Back ends the call - a bottom-anchored hint over the video (the
            flex-footer Hint needs a column parent this full-bleed layer isn't).
            Same pill geometry as the in-call name label, from callTheme. */}
        <div
          style={{
            ...videoPill(callT),
            position: "absolute",
            left: "50%",
            bottom: 40,
            transform: "translateX(-50%)",
            fontSize: 28,
          }}
        >
          ◀ Avsluta samtalet
        </div>
      </div>
    );
  }

  if (phase === "ended") {
    const copy = endedCopy(endKind, name);
    return (
      <CallOverlay palette={p}>
        <CallAvatar entry={entry} palette={p} />
        <div style={callTitleStyle(p)}>{copy.title}</div>
        <div style={callBodyStyle(p)}>{copy.body}</div>
        <div style={callHintStyle(p)}>
          Tryck OK eller ◀ för att gå tillbaka.
        </div>
      </CallOverlay>
    );
  }

  // placing + ringing - the same calm "reaching them" card.
  return (
    <CallOverlay palette={p}>
      <CallAvatar entry={entry} palette={p} ringing />
      <div style={callTitleStyle(p)}>Ringer {name}…</div>
      {entry.relation && (
        <div style={{ ...callBodyStyle(p), marginTop: 12 }}>
          {entry.relation}
        </div>
      )}
      <div style={callHintStyle(p)}>Tryck ◀ för att avbryta.</div>
      <style>{`@keyframes foyerRing{0%{transform:scale(1);opacity:0.55}70%{transform:scale(1.35);opacity:0}100%{opacity:0}}`}</style>
    </CallOverlay>
  );
}

function callTitleStyle(p: Pal): CSSProperties {
  return {
    fontSize: 68,
    fontWeight: 700,
    letterSpacing: "-0.03em",
    color: p.ink,
    // 1.1, not 1.05: "Ringer Göran…" and "Samtalet bröts" both have descenders.
    lineHeight: 1.1,
    marginTop: 40,
  };
}

/** The one explanatory line under a call title. Capped at ~40 characters per
 *  line: a longer measure on a TV read from a chair loses the reader on the
 *  carriage return. */
function callBodyStyle(p: Pal): CSSProperties {
  return {
    fontSize: 34,
    lineHeight: 1.4,
    color: p.inkSoft,
    marginTop: 20,
    maxWidth: "20em",
    marginLeft: "auto",
    marginRight: "auto",
  };
}

/** "Tryck OK eller ◀ …" - the quietest line on the card, but never below the
 *  care scheme's minimum readable size. */
function callHintStyle(p: Pal): CSSProperties {
  return {
    fontSize: 28,
    lineHeight: 1.4,
    color: p.inkMuted,
    marginTop: 32,
  };
}

/** The dimmed, centred card every non-active call phase shares. Deliberately
 *  the same geometry as the incoming ring's card (`callCard` in callTheme):
 *  a call going out and a call coming in should look like one feature. */
function CallOverlay({
  palette: p,
  children,
}: {
  palette: Pal;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(23,25,21,0.72)",
        display: "grid",
        placeItems: "center",
        padding: 80,
        zIndex: 20,
      }}
    >
      <div
        style={{
          background: p.surface,
          border: `1px solid ${p.hairSoft}`,
          borderRadius: 44,
          padding: "72px 88px",
          textAlign: "center",
          maxWidth: 1200,
          boxShadow: "0 60px 140px -50px rgba(0,0,0,0.45)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** The contact's face - with an expanding ring while we wait for an answer. */
function CallAvatar({
  entry,
  palette: p,
  ringing = false,
}: {
  entry: TvCallDirectoryEntry;
  palette: Pal;
  ringing?: boolean;
}) {
  return (
    <div
      style={{
        position: "relative",
        width: 240,
        height: 240,
        margin: "0 auto",
      }}
    >
      {ringing && (
        <span
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 999,
            border: `4px solid ${p.gCore}`,
            animation: "foyerRing 1.8s ease-out infinite",
          }}
        />
      )}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 999,
          overflow: "hidden",
          background: p.gTint,
          border: `4px solid ${p.gCore}`,
          display: "grid",
          placeItems: "center",
          color: p.gDeep,
        }}
      >
        {entry.avatarUrl ? (
          <img
            src={entry.avatarUrl}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span style={{ fontSize: 110, fontWeight: 700, lineHeight: 1 }}>
            {initialOf(entry.displayName)}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Photos slideshow (Care-P2) ────────────────────────────────────────────

interface PhotoSlideshowProps extends ScreenProps {
  realtimeEvent?: RealtimeEvent | null;
}

/**
 * Full-screen family-photo slideshow.
 *
 * Photos are fetched from /api/frontlayer/v1/premise/:id/rooms/:rid/care/photos
 * (approved only). The list auto-refreshes every 30 s and immediately when
 * a `photos_changed` realtime event arrives (within ~1 s of a new approval).
 *
 * Controls: any D-pad key / OK / Back returns to the home screen.
 * Auto-advance: 8 s per photo with a 600 ms crossfade.
 */
function PhotoSlideshow({
  go,
  scopeRef,
  context,
  realtimeEvent,
}: PhotoSlideshowProps) {
  const p = useC();
  const { photos, loading, fetchError, isGenericPreview } = useRoomPhotos(
    true,
    realtimeEvent,
    context.premiseId,
    context.roomId
  );
  const backFocused = useBackOnly(go, scopeRef, useBackTarget("photos"));

  // Which photo is shown (by index).
  const [idx, setIdx] = useState(0);
  // Crossfade opacity: true = fully visible, false = fading out.
  const [visible, setVisible] = useState(true);

  // Reset index when photo list changes (new photos arrived).
  const prevLengthRef = useRef(0);
  useEffect(() => {
    if (photos.length !== prevLengthRef.current) {
      prevLengthRef.current = photos.length;
      // Don't jump back to 0 if we're mid-slideshow - clamp instead.
      setIdx((i) => (photos.length > 0 ? Math.min(i, photos.length - 1) : 0));
    }
  }, [photos.length]);

  // Auto-advance every 8 s.
  const advance = useCallback(() => {
    if (photos.length <= 1) return;
    setVisible(false);
    setTimeout(() => {
      setIdx((i) => (i + 1) % photos.length);
      setVisible(true);
    }, 600);
  }, [photos.length]);

  useEffect(() => {
    if (photos.length <= 1) return;
    const id = setInterval(advance, 8_000);
    return () => clearInterval(id);
  }, [advance, photos.length]);

  const cur: RoomPhotoWire | undefined = photos[idx];

  // ── Generic preview (no room context) ────────────────────────────────────
  if (isGenericPreview) {
    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Head context={context} />
        <main style={subMain}>
          <SubTitle
            eyebrow="Foton från familjen"
            title="Generell förhandsvisning"
            backFocused={backFocused}
          />
          <div
            style={{
              ...careCard,
              background: p.surface,
              border: `1px solid ${p.hairSoft}`,
              flex: 1,
              padding: "56px 64px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 20,
              color: p.inkSoft,
            }}
          >
            <div style={{ fontSize: 60, color: p.gCore }}>
              <Icon.Photo size={72} />
            </div>
            <div style={{ fontSize: 36, fontWeight: 600, color: p.ink }}>
              Inga foton visas i en generell förhandsvisning
            </div>
            <p
              style={{
                fontSize: 26,
                maxWidth: 760,
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              Öppna förhandsvisningen från en specifik enhet i enhetslistan för
              att se rummets foton.
            </p>
          </div>
        </main>
        <Hint keys={[{ cap: "◀", label: "Tillbaka" }]} />
      </div>
    );
  }

  // ── Empty state ───────────────────────────────────────────────────────────
  if (!loading && photos.length === 0) {
    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Head context={context} />
        <main style={subMain}>
          <SubTitle
            eyebrow="Foton från familjen"
            title="Inga foton ännu"
            backFocused={backFocused}
          />
          <div
            style={{
              ...careCard,
              background: p.surface,
              border: `1px solid ${p.hairSoft}`,
              flex: 1,
              padding: "56px 64px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 28,
              color: p.inkSoft,
            }}
          >
            <div style={{ fontSize: 60, color: p.gCore }}>
              <Icon.Photo size={72} />
            </div>
            <div style={{ fontSize: 36, fontWeight: 600, color: p.ink }}>
              Inga foton har skickats ännu
            </div>
            <p
              style={{
                fontSize: 26,
                maxWidth: 720,
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              Be dina nära och kära skicka foton via e-post - de visas direkt
              här på TV-skärmen.
            </p>
            {fetchError && (
              <p
                style={{
                  fontFamily: p.mono,
                  fontSize: 18,
                  color: p.inkMuted,
                  margin: 0,
                  maxWidth: 860,
                  wordBreak: "break-all",
                }}
              >
                {fetchError}
              </p>
            )}
          </div>
        </main>
        <Hint keys={[{ cap: "◀", label: "Tillbaka" }]} />
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading && photos.length === 0) {
    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Head context={context} />
        <main style={subMain}>
          <SubTitle eyebrow="Foton" title="Laddar…" backFocused={backFocused} />
        </main>
        <Hint keys={[{ cap: "◀", label: "Tillbaka" }]} />
      </div>
    );
  }

  // ── Full-screen slideshow ─────────────────────────────────────────────────
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "#000",
        overflow: "hidden",
      }}
    >
      {/* Photo */}
      {cur && (
        <img
          key={cur.photoId}
          src={cur.imageUrl}
          alt={cur.caption || cur.subject || "Foto från familjen"}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            opacity: visible ? 1 : 0,
            transition: "opacity 600ms ease",
          }}
        />
      )}

      {/* Gradient scrim at the bottom so text reads on bright photos */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "40%",
          background:
            "linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Caption + attribution */}
      {cur && (
        <div
          style={{
            position: "absolute",
            left: 72,
            right: 72,
            bottom: 100,
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {cur.caption && (
            <p
              style={{
                fontSize: 38,
                fontFamily: p.sans,
                fontWeight: 500,
                margin: 0,
                lineHeight: 1.3,
                textShadow: "0 2px 8px rgba(0,0,0,0.6)",
              }}
            >
              {cur.caption}
            </p>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                fontFamily: p.mono,
                fontSize: 20,
                letterSpacing: "0.1em",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              {cur.fromName ? `Från ${cur.fromName}` : cur.fromEmail}
            </span>
            {photos.length > 1 && (
              <span
                style={{
                  fontFamily: p.mono,
                  fontSize: 18,
                  letterSpacing: "0.1em",
                  color: "rgba(255,255,255,0.5)",
                }}
              >
                {idx + 1} / {photos.length}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Pagination dots (max 12 shown) */}
      {photos.length > 1 && photos.length <= 12 && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            bottom: 68,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            gap: 10,
          }}
        >
          {photos.map((_, i) => (
            <span
              key={i}
              style={{
                width: i === idx ? 24 : 8,
                height: 8,
                borderRadius: 999,
                background: i === idx ? "#fff" : "rgba(255,255,255,0.4)",
                transition: "width 300ms ease, background 300ms ease",
              }}
            />
          ))}
        </div>
      )}

      {/* Hint bar - pinned to bottom, overlays the photo */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0 }}>
        <Hint noBorder keys={[{ cap: "◀", label: "Tillbaka" }]} />
      </div>
    </div>
  );
}

// ── TV launch ─────────────────────────────────────────────────────────────

function TvLaunch({ go, scopeRef, context }: ScreenProps) {
  const p = useC();
  useFocusGrid({
    rows: 1,
    cols: 1,
    scopeRef,
    onEnter: () => go("home"),
    onBack: () => go("home"),
  });
  // Open live TV when this screen mounts. Where that goes - the installed
  // vendor streaming app, or Foyer's own player where the premise has been
  // switched to it (`PremiseConfig.tvPlayer`, TV-6) - is decided in one place,
  // `lib/openTv.ts`. No-op on web / CMS preview (nothing to launch).
  //
  // The hand-copied package literal that used to sit here, under a comment
  // asking the next reader to keep it in sync with the launcher kit by hand, is
  // gone: it was the fifth copy, and the reason the CMS's own streaming-app
  // setting had no effect on any of them.
  //
  // ⚠️ No `openBuiltin`, deliberately, and this is not an oversight to fix
  // later. Hemvist declares neither `showVod` nor `showGuide` (`manifests.ts`,
  // beside `CARE_HOME_KNOBS`): the film catalogue is an entitled commercial
  // catalogue a care premise buys none of, and an EPG grid is the opposite of
  // the oversized-type brief. There is therefore no built-in screen to open
  // here, so care keeps its one big "TV" tile and the vendor app behind it -
  // even for a premise whose `tvPlayer` says "foyer". `resolveTvTarget` checks
  // `builtinAvailable` BEFORE the premise's choice for exactly this case.
  // A care guide would be a DIFFERENT screen (a large "what is on now" list),
  // not this one behind a knob.
  useEffect(() => {
    openTvForThisScreen();
  }, []);
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Head context={context} />
      <main
        style={{
          flex: "1 1 auto",
          display: "grid",
          placeItems: "center",
          padding: "0 72px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 32,
            textAlign: "center",
          }}
        >
          <span
            style={{
              width: 160,
              height: 160,
              borderRadius: 40,
              display: "grid",
              placeItems: "center",
              background: p.gDeep,
              color: p.gGlow,
            }}
          >
            <Icon.Tv size={88} />
          </span>
          <div>
            <div
              style={{
                fontFamily: p.mono,
                fontSize: 16,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: p.gCore,
              }}
            >
              01 · TV
            </div>
            <h1
              style={{
                fontSize: 96,
                fontWeight: 700,
                letterSpacing: "-0.04em",
                lineHeight: 1,
                margin: "10px 0 0",
                color: p.ink,
              }}
            >
              Startar TV…
            </h1>
          </div>
          <p
            style={{
              fontSize: 26,
              color: p.inkSoft,
              maxWidth: 720,
              lineHeight: 1.4,
              margin: 0,
            }}
          >
            TV-appen öppnas på den här skärmen. Tryck <strong>◀</strong> för att
            gå tillbaka till menyn.
          </p>
        </div>
      </main>
      <Hint
        keys={[{ cap: "◀", label: "Tillbaka" }]}
        right={context.hotelName}
      />
    </div>
  );
}
